import React, { useEffect, useState, useCallback, useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import CollapsibleTable from '../../components/table';
import axios from 'axios';
import config from '../../config/config';
import { useSession } from '../../components/hooks/useSession';
import SimpleSuccessSpinner from '../../components/LoadingSpinner';
import SolicitudProgreso from '../../components/ModalSeguimiento';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';
import ExportButton from '../../components/BotonEx';
import FileActionModal from '../../components/FileActionModal';
import { buildFilterPayload } from '../../utils/filterUtils';

import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FolderIcon from '@mui/icons-material/Folder';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { TableFilterChips } from '../../components/Table/components/TableFilterChips';
import CancelacionModal from '../../components/CancelacionModal';
import FileUploadModal from '../../components/FileUploadModal';
import { cancelarPago } from '../../services/pagosAutService';
import DoDisturbOffIcon from '@mui/icons-material/DoDisturbOff';
import AttachFileIcon from '@mui/icons-material/AttachFile';

// ── POPOVER AISLADO: no re-renderiza la tabla completa cuando se abre/cierra ──
const TotalesPopover = forwardRef(function TotalesPopover({ totalesRef }, ref) {
  const [anchor, setAnchor] = useState(null);
  const [field, setField] = useState(null);

  useImperativeHandle(ref, () => ({
    open: (el, fieldKey) => {
      setAnchor(el);
      setField(fieldKey);
    },
    close: () => {
      setAnchor(null);
      setField(null);
    }
  }), []);

  const totalesPorDivisa = totalesRef?.current || {};
  const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={() => { setAnchor(null); setField(null); }}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: {
          p: 1.5,
          borderRadius: '12px',
          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.18), 0 8px 10px -6px rgba(0,0,0,0.1)',
          border: '1px solid #cbd5e1',
          minWidth: 260,
          bgcolor: '#ffffff',
        }
      }}
    >
      <Box sx={{ mb: 1, pb: 0.5, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography sx={{ fontSize: 11, fontWeight: 900, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {field === 'SOLICITADO' ? 'Detalle Solicitado por Divisa' : 'Detalle Total OC por Divisa'}
        </Typography>
        <Chip label="Detallado" size="small" sx={{ height: 16, fontSize: 8.5, fontWeight: 800, bgcolor: '#e0f2fe', color: '#0369a1' }} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
        {Object.entries(totalesPorDivisa).map(([divisa, vals]) => {
          const total = vals.total || 0;
          const solicitado = vals.solicitado || 0;
          const restante = total > solicitado ? total - solicitado : (vals.restante || 0);
          const valToShow = field === 'SOLICITADO' ? solicitado : total;

          const colorMain =
            divisa === 'USD' ? '#b45309' :
            divisa === 'EUR' ? '#4338ca' :
            divisa === 'MXN' ? '#0369a1' : '#374151';
          const bgBadge =
            divisa === 'USD' ? '#fef3c7' :
            divisa === 'EUR' ? '#e0e7ff' :
            divisa === 'MXN' ? '#e0f2fe' : '#f3f4f6';

          return (
            <Box
              key={divisa}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                p: 0.8,
                borderRadius: '8px',
                bgcolor: '#f8fafc',
                border: '1px solid #f1f5f9',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={divisa}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 9.5,
                    fontWeight: 900,
                    color: colorMain,
                    bgcolor: bgBadge,
                    '& .MuiChip-label': { px: 0.8 },
                  }}
                />
                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: '#64748b' }}>
                  ({vals.count || 0} OCs)
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 900, color: field === 'SOLICITADO' ? '#16a34a' : '#0f172a', fontVariantNumeric: 'tabular-nums' }}>
                  ${fmt(valToShow)}
                </Typography>
                {field === 'SOLICITADO' && (
                  <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#ea580c' }}>
                    Restante: ${fmt(restante)}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Popover>
  );
});

const MobileTotalsFooter = React.memo(function MobileTotalsFooter({ divisas, totalCount, onOpenPopover }) {
  const [expanded, setExpanded] = useState(false);
  if (!divisas || Object.keys(divisas).length === 0) return null;

  const displayTotalCount = totalCount || 0;
  const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const getBadgeStyle = (div) => {
    if (div === 'USD') return { color: '#b45309', bg: '#fef3c7' };
    if (div === 'EUR') return { color: '#4338ca', bg: '#e0e7ff' };
    if (div === 'MXN') return { color: '#0369a1', bg: '#e0f2fe' };
    if (div === 'GBP') return { color: '#701a75', bg: '#fae8ff' };
    if (div === 'CAD') return { color: '#991b1b', bg: '#fee2e2' };
    return { color: '#334155', bg: '#f1f5f9' };
  };

  const getCurrencySymbol = (div) => {
    if (div === 'EUR') return '€';
    if (div === 'GBP') return '£';
    return '$';
  };

  const divisasEntries = Object.entries(divisas || {}).sort((a, b) => {
    const order = { MXN: 1, USD: 2, EUR: 3 };
    return (order[a[0]] || 99) - (order[b[0]] || 99);
  });

  return (
    <Paper
      elevation={3}
      sx={{
        mt: 'auto',
        pt: 0,
        position: 'sticky',
        bottom: 2,
        zIndex: 10,
        borderRadius: '14px',
        border: '1.5px solid #cbd5e1',
        bgcolor: '#ffffff',
        boxShadow: '0 4px 18px rgba(15, 23, 42, 0.12)',
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={() => setExpanded(prev => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.4,
          py: 0.8,
          bgcolor: '#f1f5f9',
          borderBottom: expanded ? '1px solid #e2e8f0' : 'none',
          cursor: 'pointer',
          userSelect: 'none',
          '&:active': { bgcolor: '#e2e8f0' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.74rem', color: '#0f172a', letterSpacing: '0.02em' }}>
            TOTALES ({displayTotalCount} OCs)
          </Typography>
          <Chip
            label={`${divisasEntries.length} Divisas`}
            size="small"
            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800, bgcolor: '#e0f2fe', color: '#0369a1' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
          <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#0284c7' }}>
            {expanded ? 'Ocultar' : 'Ver detalle'}
          </Typography>
          <IconButton size="small" sx={{ p: 0.1, color: '#0284c7' }}>
            {expanded ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Box>
      </Box>

      {/* Resumen en 1 sola fila cuando está comprimido */}
      {!expanded && (
        <Box
          onClick={() => setExpanded(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
            px: 1.4,
            py: 0.7,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            bgcolor: '#ffffff',
            cursor: 'pointer'
          }}
        >
          {divisasEntries.map(([divisa, vals]) => {
            const { color, bg } = getBadgeStyle(divisa);
            const sym = getCurrencySymbol(divisa);
            return (
              <Box key={divisa} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Chip
                  label={divisa}
                  size="small"
                  sx={{ height: 16, fontSize: '0.58rem', fontWeight: 850, bgcolor: bg, color: color }}
                />
                <Typography sx={{ fontSize: '0.74rem', fontWeight: 850, color: '#0f172a', whiteSpace: 'nowrap' }}>
                  {sym}{fmt(vals.total || 0)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Desglose completo cuando está expandido */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 1.2, bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 1 }}>
          {divisasEntries.map(([divisa, vals]) => {
            const total = vals.total || 0;
            const sol = vals.solicitado || 0;
            const rest = vals.restante !== undefined ? vals.restante : (total > sol ? total - sol : 0);
            const { color, bg } = getBadgeStyle(divisa);
            const sym = getCurrencySymbol(divisa);

            return (
              <Box
                key={divisa}
                sx={{
                  p: 1,
                  bgcolor: '#ffffff',
                  borderRadius: '10px',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.03)'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Chip
                      label={divisa}
                      size="small"
                      sx={{ height: 18, fontSize: '0.62rem', fontWeight: 900, bgcolor: bg, color: color }}
                    />
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b' }}>
                      {vals.count || 0} OCs
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color: '#0f172a' }}>
                    {sym}{fmt(total)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8, pt: 0.6, borderTop: '1px dashed #e2e8f0' }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 750, color: '#64748b', textTransform: 'uppercase' }}>
                      Solicitado
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#16a34a' }}>
                      {sym}{fmt(sol)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 750, color: '#64748b', textTransform: 'uppercase' }}>
                      Restante
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#ea580c' }}>
                      {sym}{fmt(rest)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
});

/* ===================== MAPAS BASE TABLA + EXCEL ===================== */
const exportLabels = {
  FOLIO: 'ORDEN COMPRA',
  REQUI: 'REQUISICION',
  EMPRESA: 'EMPRESA',
  FECHA_OC: 'FECHA OC',
  FECHA_SOLICITUD: 'FECHA SOLICITUD',
  REQUISITOR: 'REQUISITOR',
  PROVEEDOR: 'PROVEEDOR',
  SOLICITANTE: 'COMPRADOR',
  NUMEROCC: 'NUMERO CENTRO COSTO',
  CENTROCOSTO: 'CENTRO COSTO',
  DEPARTAMENTO: 'DEPARTAMENTO',
  TOTAL: 'TOTAL OC',
  DIVISA: 'DIVISA',
  DESCRIPCION: 'DESCRIPCION',
  DESCRIPCION_PART: 'DESCRIPCION',
  MONTOSOLICITADO: 'SOLICITADO',
  PENDIENTE: 'RESTANTE',
  NOTAS_OBSERVACIONES: 'NOTAS OBSERVACIONES',
  EXHIBIT: 'EXHIBIT',
  FOLIO_FACTURA: 'FACTURAS',
  NUMERO_ECONOMICO: 'NUMERO ECONOMICO',
  ESTATUS: 'ESTATUS',
  FECHA_PAGO: 'FECHA DE PAGO',
  CODIGO_PART: 'Código Partida',
  CANTIDAD: 'Cantidad',
  COSTOU: 'Costo Unitario',
  TOTAL_PART: 'Total Partida',
};

/* ===================== CONFIGURACIÓN DE CHIPS ===================== */
const statusChipsConfig = [
  { label: 'Pendiente', value: 'Proceso', bg: '#fffbeb', color: '#92400e', help: 'La OC fue creada y está en proceso de autorización.' },
  { label: 'Autorizada', value: 'Autorizada', bg: '#eff6ff', color: '#1e40af', help: 'Todos aprobaron la OC; queda pendiente el pago' },
  { label: 'Pagada', value: 'Pagada', bg: '#f0fdf4', color: '#166534', help: 'La OC ya fue pagada.' },
  { label: 'Rechazada', value: 'Cancelada', bg: '#fef2f2', color: '#991b1b', help: 'La OC fue rechazada o regresada por un autorizador.' },
  { label: 'Vencida', value: 'Vencida', bg: '#fff7ed', color: '#c2410c', help: 'La OC ha superado su fecha de vencimiento.' },
  { label: 'Cancelada', value: 'Cancelada ERP', bg: '#fdf2f8', color: '#9d174d', help: 'La OC fue cancelada desde el ERP.' },
  { label: 'Default', value: 'Default', bg: '#f8fafc', color: '#475569', help: 'La OC aún no ha iniciado ningún proceso.' },
];

/* ===================== HELPERS ===================== */
const getMimeTypeFromExtension = (ext) => {
  switch (ext) {
    case 'pdf':
      return 'application/pdf';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
    case 'xml':
      return 'application/xml';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'ppt':
      return 'application/vnd.ms-powerpoint';
    case 'pptx':
      return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    default:
      return 'application/octet-stream';
  }
};

const deriveCategory = (filepath = '') => {
  const normalized = filepath.replace(/\\/g, '/').toLowerCase();
  if (normalized.includes('/comprobante pagos/') || normalized.includes('/comprobante pago/')) return 'COMPROBANTE';
  if (normalized.includes('/factura/') || normalized.includes('\\factura\\')) return 'FACTURA';
  if (normalized.includes('/datos bancarios/') || normalized.includes('datos_bancarios') || normalized.includes('caratula')) return 'DATOS BANCARIOS';
  if (normalized.includes('/orden') || normalized.includes('ordencompra') || normalized.includes('/oc/')) return 'OC';
  return 'OTROS';
};

const buildNoAdjuntoMessage = (row) => {
  const referencia = row?.REFERENCIA?.toString().trim();
  const metodoPago = row?.METODOPAGO?.toString().trim();
  const fechaPago = row?.FECHA_PAGO;

  const metodoNormalizado = (metodoPago || '').toLowerCase();
  const esTarjetaCredito =
    metodoNormalizado.includes('tarjeta') &&
    metodoNormalizado.includes('credito');

  if (esTarjetaCredito && referencia && fechaPago) {
    return `No hay comprobante adjunto. El pago fue registrado con método ${metodoPago}, referencia ${referencia} y fecha de pago ${fechaPago}.`;
  }

  if (esTarjetaCredito && referencia) {
    return `No hay comprobante adjunto. El pago fue registrado con método ${metodoPago} y referencia ${referencia}.`;
  }

  if (esTarjetaCredito && fechaPago) {
    return `No hay comprobante adjunto. El pago fue registrado con método ${metodoPago} y fecha de pago ${fechaPago}.`;
  }

  if (esTarjetaCredito) {
    return `No hay comprobante adjunto. El método de pago es ${metodoPago}, por lo que puede no requerir archivo comprobante.`;
  }

  if (referencia && fechaPago) {
    return `No hay comprobante adjunto. El pago cuenta con referencia registrada: ${referencia} y fecha de pago ${fechaPago}.`;
  }

  if (referencia) {
    return `No hay comprobante adjunto. El pago cuenta con referencia registrada: ${referencia}.`;
  }

  if (fechaPago) {
    return `No hay comprobante adjunto. El pago cuenta con fecha de pago registrada: ${fechaPago}.`;
  }

  if (metodoPago) {
    return `No hay comprobante adjunto. El método de pago registrado es: ${metodoPago}.`;
  }

  return 'No hay comprobante adjunto ni información adicional de pago registrada.';
};

// ✅ Normaliza idEmpresa que puede venir como:
//  - [2]
//  - "2"
//  - "[1,2,3]"
//  - "1,2,3"
const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];

  if (Array.isArray(idEmpresa)) {
    return idEmpresa
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
  }

  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();

    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) {
          return arr
            .map((x) => Number(x))
            .filter((n) => Number.isFinite(n) && n > 0);
        }
      } catch {}
    }

    if (s.includes(',')) {
      return s
        .split(',')
        .map((x) => Number(x.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
    }

    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }

  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

const buildEmpresaScope = (idEmpresa) => {
  const arr = normalizeEmpresaArray(idEmpresa)
    .filter((n) => Number.isFinite(n) && n > 0)
    .sort((a, b) => a - b);

  return arr.length ? arr.join(',') : '0';
};

/* ===================== EXPORT: FECHAS AUTORIZACIÓN ===================== */
const pad2 = (n) => String(n).padStart(2, '0');

const formatFechaExcel = (fecha) => {
  if (!fecha) return '';
  const d = new Date(fecha);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const formatInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
  const today = new Date();
  return {
    start: formatInputDate(new Date(today.getFullYear(), today.getMonth(), 1)),
    end: formatInputDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
  };
};

const DEFAULT_SORT_FIELD = 'FECHASOLICITUD';
const DEFAULT_SORT_DIR = 'DESC';

const getUniqueOptions = (values) => (
  Array.from(new Set(values.map(value => String(value ?? '').trim()).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, 'es'))
);

const buildCentroCostoOption = (row = {}) => {
  const numero = String(row.NUMEROCC || row.NUMERO_CENTRO_COSTO || row.NUMERO_CC || '').trim();
  const nombre = String(row.CENTROCOSTO || row.CENTRO_COSTO || row.NOMBRE_CC || '').trim();

  if (numero && nombre && numero.toUpperCase() !== nombre.toUpperCase()) return `${numero} - ${nombre}`;
  return numero || nombre;
};

const splitCentroCostoOption = (value = '') => {
  const str = String(value || '').trim();
  if (!str) return [];
  const idx = str.indexOf(' - ');
  if (idx !== -1) {
    const num = str.substring(0, idx).trim();
    const nom = str.substring(idx + 3).trim();
    return [num, nom].filter(Boolean);
  }
  return [str];
};

const getCentroCostoFilterValue = (value = '') => {
  const parts = splitCentroCostoOption(value);
  return parts.length > 1 ? parts[0] : String(value).trim();
};

const getCentroCostoFilterValues = (values = []) => {
  const result = [];
  (values || []).forEach(val => {
    const parts = splitCentroCostoOption(val);
    parts.forEach(p => {
      if (p && !result.includes(p)) result.push(p);
    });
  });
  return result;
};

const normalizeOptionText = (value = '') => (
  String(value ?? '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
);

const matchesAnyOption = (value, selected = []) => {
  if (!selected.length) return true;
  const normalizedValue = normalizeOptionText(value);
  return selected.some(item => {
    const normalizedItem = normalizeOptionText(item);
    return normalizedValue === normalizedItem ||
      normalizedValue.includes(normalizedItem);
  });
};

const getSelectedSummary = (values = []) => {
  if (!values.length) return '';
  if (values.length === 1) {
    const first = values[0];
    return typeof first === 'string' ? first : first?.nombre || String(first);
  }
  return `${values.length} seleccionadas`;
};

const renderSummaryInput = (params, label, selectedValues = []) => {
  const summary = getSelectedSummary(selectedValues);
  const isSelected = Boolean(summary);

  return (
    <TextField
      {...params}
      placeholder={summary ? undefined : label}
      InputProps={{
        ...params.InputProps,
        startAdornment: summary ? (
          <Box
            component="span"
            sx={{
              maxWidth: 'calc(100% - 24px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: '0.68rem',
              fontWeight: 700,
              color: '#0655A5',
            }}
          >
            {summary}
          </Box>
        ) : params.InputProps.startAdornment,
      }}
      sx={{
        ...(isSelected ? {
          '& .MuiInputBase-root': {
            bgcolor: '#f0f7ff !important',
            borderColor: '#93c5fd !important',
            '& fieldset': {
              borderColor: '#60a5fa !important',
              borderWidth: '1.5px !important',
            }
          }
        } : {})
      }}
    />
  );
};

const icon = <CheckBoxOutlineBlankIcon fontSize="small" sx={{ fontSize: 16, color: '#94a3b8' }} />;
const checkedIcon = <CheckBoxIcon fontSize="small" sx={{ fontSize: 16, color: '#0655A5' }} />;

const renderCheckboxOption = (props, option, { selected }) => (
  <li
    {...props}
    style={{
      padding: '4px 8px',
      fontSize: '0.74rem',
      fontWeight: selected ? 700 : 500,
      backgroundColor: selected ? '#e0f2fe' : 'transparent',
      color: selected ? '#0369a1' : '#1e293b',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      borderBottom: '1px solid #f8fafc',
    }}
  >
    <Checkbox
      icon={icon}
      checkedIcon={checkedIcon}
      style={{ marginRight: 4, padding: 0 }}
      checked={selected}
      size="small"
    />
    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
      {typeof option === 'string' ? option : option?.nombre || ''}
    </Box>
  </li>
);

const compactAutocompleteSx = {
  '& .MuiInputBase-root': {
    height: 28,
    minHeight: 28,
    maxHeight: 28,
    borderRadius: '10px !important',
    bgcolor: '#ffffff',
    fontSize: '0.68rem',
    fontWeight: 500,
    px: '10px !important',
    py: '0px !important',
    flexWrap: 'nowrap',
    overflow: 'visible',
    '& fieldset': { 
      borderColor: '#cbd5e1 !important',
      top: 0,
      '& legend': { display: 'none' }
    },
    '&:hover fieldset': { borderColor: '#94a3b8 !important' },
    '&.Mui-focused fieldset': { borderColor: '#0655A5 !important', borderWidth: '1.5px !important' },
  },
  '& .MuiAutocomplete-input': {
    minWidth: '24px !important',
    py: '0px !important',
    fontSize: '0.68rem',
    color: '#0f172a',
    '&::placeholder': {
      color: '#64748b',
      opacity: 1,
      fontWeight: 500,
    }
  },
  '& .MuiInputLabel-root': {
    display: 'none',
  },
};

const compactTextFieldSx = {
  '& .MuiInputBase-root': {
    height: 36,
  },
  '& .MuiInputBase-input': {
    py: 0.55,
    fontSize: 13,
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
  },
};

const pickMaxDateIso = (fechas) => {
  const dates = (fechas || [])
    .map((f) => new Date(f))
    .filter((d) => !isNaN(d.getTime()))
    .sort((a, b) => b - a);

  return dates.length ? dates[0].toISOString() : '';
};

const buildFechasAutExport = (row) => {
  const cadena = Array.isArray(row?.cadenaAut) ? row.cadenaAut : [];

  const aut = cadena.filter((x) => String(x?.ROL || '').startsWith('Autorizador '));
  const teso = cadena.filter((x) => String(x?.ROL || '').startsWith('Tesorero '));

  const autOk = aut.filter((x) => x?.ESTADO === 'CHECK_GREEN' && x?.FECHA_AUTORIZACION);
  const FECHA_AUT_ULTIMA = formatFechaExcel(
    pickMaxDateIso(autOk.map((x) => x.FECHA_AUTORIZACION))
  );

  const fields = { FECHA_AUT_ULTIMA };

  for (const item of aut) {
    const rol = String(item?.ROL || '');
    const match = rol.match(/Autorizador\s+(\d+)/i);
    if (!match) continue;

    const nivel = Number(match[1]);
    const key = `FECHA_AUT_N${nivel}`;

    if (item?.ESTADO === 'CHECK_GREEN') {
      fields[key] = formatFechaExcel(item?.FECHA_AUTORIZACION);
    } else if (!(key in fields)) {
      fields[key] = '';
    }
  }

  for (const item of teso) {
    const rol = String(item?.ROL || '');
    const match = rol.match(/Tesorero\s+(\d+)/i);
    if (!match) continue;

    const nivel = Number(match[1]);
    const key = `FECHA_TESO_N${nivel}`;

    if (item?.ESTADO === 'CHECK_GREEN') {
      fields[key] = formatFechaExcel(item?.FECHA_AUTORIZACION);
    } else if (!(key in fields)) {
      fields[key] = '';
    }
  }

  return fields;
};

export default function SolicitudesProgramadas() {
  const { loggedInUsername, nombreEmpresa, idEmpresa, idPerfil, nombrePerfil } = useSession();
  const apiUrl = config.apiBaseUrl;

  const usuarioId = useMemo(() => {
    return Number(sessionStorage.getItem('UsuarioId') || 0);
  }, []);

  const esTesorero = useMemo(() => {
    return typeof nombrePerfil === 'string' && 
      nombrePerfil.toLowerCase().includes('tesorero');
  }, [nombrePerfil]);

  const esCompras = useMemo(() => {
    return typeof nombrePerfil === 'string' && 
      nombrePerfil.toLowerCase().includes('compras');
  }, [nombrePerfil]);

  const empresaScope = useMemo(() => buildEmpresaScope(idEmpresa), [idEmpresa]);

  const empresaIds = useMemo(() => {
    if (!empresaScope || empresaScope === '0') return [];
    return empresaScope
      .split(',')
      .map((n) => Number(n))
      .filter((x) => Number.isFinite(x) && x > 0);
  }, [empresaScope]);

  const [data, setData] = useState({ contenido: [] });
  const totalesPopoverRef = useRef(null);
  const dataTotalesRef = useRef({});
  const dataTotalCountRef = useRef(0);

  useEffect(() => {
    dataTotalesRef.current = data?.totalesPorDivisa || {};
    dataTotalCountRef.current = data?.total || 0;
  }, [data?.totalesPorDivisa, data?.total]);

  const empresaIdPersist = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.length === 1 ? arr[0] : 0;
  }, [idEmpresa]);

  const hiddenFieldsMemo = useMemo(() => {
    const base = [
      'DIAS_VENCIMIENTO', 'DIAS_VENCIMINETO', 'DIASRESTANTES', 'DIAS_VENCIMIENTO_RAW',
      'VENCIMIENTO', 'REFERENCIA', 'cadenaAut', 'CANCELADO_ERP', 'usuarioCancelo',
      'ID_OC', 'HAS_OC', 'ID_CB', 'HAS_CB', 'ID_FACTURA', 'HAS_FACTURA'
    ];

    if (esTesorero) {
      return [
        ...base,
        'DEPARTAMENTO',
        'METODOPAGO',
        'SOLICITANTE',
        'REQUISITOR',
        'FOLIO_FACTURA',
        'DIVISA',
        'PENDIENTE',
      ];
    }

    return [...base];
  }, [esTesorero]);

  const compactTableHiddenFields = useMemo(() => ([
    'ID',
    'FOLIO',
    'REQUI',
    'EMPRESA',
    'SOLICITANTE',
    'REQUISITOR',
    'DEPARTAMENTO',
    'METODOPAGO',
    'FOLIO_FACTURA',
    'PENDIENTE',
    'CONCEPTOPAGO',
    'NOTA',
    'FECHA_PAGO',
    'CENTROCOSTO',
    'NUMEROCC',
    'PROVEEDOR',
    'FECHASOLICITUD',
    '_FECHASOLICITUD_SORT',
    'TOTAL',
    'MONTOSOLICITADO',
    'DIVISA',
    'ESTATUS',
    'NUMERO_ECONOMICO',
    'ECONOMICO',
    'PAGO',
    'TIPO_PAGO',
    'TIPOPAGO',
    'TIPO_DE_PAGO',
    'MONTO_DIVISA',
  ]), []);

  const tableHiddenFieldsMemo = useMemo(() => ([
    ...hiddenFieldsMemo,
    ...compactTableHiddenFields,
  ]), [hiddenFieldsMemo, compactTableHiddenFields]);

  // ✅ Labels estables para la tabla (no dinámicos)
  const tableColumnLabels = useMemo(() => ({
    OC_REQUI: 'OC / Requi',
    FECHA_SOLICITANTE: 'Fecha / Solicitante',
    PROVEEDOR_COMPRADOR: 'Proveedor / Comprador',
    CENTRO_DEPTO: 'Centro / Departamento',
    TOTAL_DIVISA: 'Total / Divisa',
    MONTO_RESTANTE: 'Solicitado / Rest.',
    EXHIBIT: 'Exhibit',
    FACTURA_COMPACTA: 'Factura',
    ESTATUS_COMPACTO: 'Estatus',
    FOLIO: 'Orden',
    FECHASOLICITUD: 'Fecha OC',
    NUMEROCC: 'Numero Centro Costo',
    CENTROCOSTO: 'Nombre CC',
    DEPARTAMENTO: 'Solicita',
    SOLICITANTE: 'Comprador',
    REQUISITOR: 'Requisitor',
    FOLIO_FACTURA: 'Factura',
    NUMERO_ECONOMICO: 'Economico',
    TOTAL: 'Total OC',
    MONTOSOLICITADO: 'Programar a Pago',
    CONCEPTOPAGO: 'Condicion Pago',
    NOTA: 'Nota Requi',
    METODOPAGO: 'Metodo Pago',
    REQUI: 'Requi',
    ESTATUS: 'Estado',
    FECHAPAGO: 'Fecha Pago',
  }), []);

  const compactColumnOrder = useMemo(() => ([
    'OC_REQUI',
    'FECHA_SOLICITANTE',
    'PROVEEDOR_COMPRADOR',
    'CENTRO_DEPTO',
    'TOTAL_DIVISA',
    'MONTO_RESTANTE',
    'EXHIBIT',
    'FACTURA_COMPACTA',
    'ESTATUS_COMPACTO',
  ]), []);

  const [empresaOptions, setEmpresaOptions] = useState([]);
  const [filterOptionRows, setFilterOptionRows] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingAction, setLoadingAction] = useState(false);
  const [error, setError] = useState(null);

  const [colorMode, setColorMode] = useState('full');
  const [anchorElColorMode, setAnchorElColorMode] = useState(null);

  const [openAuthModal, setOpenAuthModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const [fileModalOpen, setFileModalOpen] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [initialFileId, setInitialFileId] = useState(null);

  const [modalStates, setModalStates] = useState({
    openCancelacionModal: false,
    observaciones: '',
    cancelarID: null,
    cancelarEmpresa: null,
    diasRestantes: null,
    folio: '',
    proveedor: '',
    fechaSolicitud: '',
    monto: '',
    fechaVencimiento: '',
    departamento: '',
    centroCostos: '',
  });

  const [fileUploadModalOpen, setFileUploadModalOpen] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);


  const defaultMonthRange = useMemo(() => getCurrentMonthRange(), []);

  const baseFilters = useMemo(() => {
    const initialEmpId = empresaIds.length === 1 ? empresaIds[0] : null;
    const initialEmpName = initialEmpId ? nombreEmpresa : '';

    return {
      FECHA_INICIO: defaultMonthRange.start,
      FECHA_FIN: defaultMonthRange.end,
      FOLIO: '',
      EMPRESA: initialEmpName,
      EMPRESA_IDS: initialEmpId ? [initialEmpId] : [],
      PROVEEDOR: '',
      PROVEEDORES: [],
      DIVISAS: [],
      CENTRO_COSTO: '',
      CENTRO_COSTOS: [],
      DEPARTAMENTO: '',
      DEPARTAMENTOS: [],
      ESTATUS: '',
      PROYECTO: '',
      PROYECTOS: [],
      DIVISION: '',
      DIVISIONES: [],
    };
  }, [defaultMonthRange, empresaIds, nombreEmpresa]);

  const [tempFilterData, setTempFilterData] = useState(baseFilters);
  const [filterData, setFilterData] = useState(baseFilters);
  const [quickSearch, setQuickSearch] = useState('');
  const [showAnalyticsCards, setShowAnalyticsCards] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [hierarchicalAnchorEl, setHierarchicalAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  // ─── Catálogos para filtros dinámicos (Empresa -> Proyecto -> Grupo -> CC) ───
  const [proyectosCatalogo, setProyectosCatalogo] = useState([]);
  const [divisionesCatalogo, setDivisionesCatalogo] = useState([]);
  const [centrosCostoCatalogo, setCentrosCostoCatalogo] = useState([]);

  const selectedEmpresaIds = useMemo(() => {
    const selected = Array.isArray(filterData.EMPRESA_IDS)
      ? filterData.EMPRESA_IDS.map(Number).filter(id => Number.isInteger(id) && id > 0)
      : [];
    const scopedSelected = selected.filter(id => empresaIds.includes(id));
    if (scopedSelected.length) return scopedSelected;
    return empresaIds;
  }, [empresaIds, filterData.EMPRESA_IDS]);

  const selectedCatalogEmpresaIds = useMemo(() => {
    const selected = Array.isArray(tempFilterData.EMPRESA_IDS)
      ? tempFilterData.EMPRESA_IDS.map(Number).filter(id => Number.isInteger(id) && id > 0)
      : [];
    const scopedSelected = selected.filter(id => empresaIds.includes(id));
    if (scopedSelected.length) return scopedSelected;
    return empresaIds;
  }, [empresaIds, tempFilterData.EMPRESA_IDS]);

  const handleStatusFilterClick = useCallback((status) => {
    // Si el estatus clickeado ya es el activo, lo limpiamos (asignamos vacío)
    const newVal = tempFilterData.ESTATUS === status ? '' : status;
    setTempFilterData(prev => {
      const updated = { ...prev, ESTATUS: newVal };
      // Opcionalmente ejecutamos la búsqueda de inmediato
      setFilterData(updated);
      setTableParams(prev => ({ ...prev, page: 1 }));
      return updated;
    });
  }, [tempFilterData.ESTATUS]);

  // ✅ Unificación de estados de la tabla (Paginación, Orden y Búsqueda Global)
  const [tableParams, setTableParams] = useState({
    page: 1,
    pageSize: 25,
    globalQuery: '',
    sortField: DEFAULT_SORT_FIELD,
    sortDir: DEFAULT_SORT_DIR
  });
  const tableParamsRef = useRef(tableParams);
  tableParamsRef.current = tableParams;

  const applyFilters = useCallback((nextFilters = tempFilterData, nextQuickSearch = quickSearch) => {
    const cleanSearch = String(nextQuickSearch || '').trim();
    const updatedParams = {
      ...tableParamsRef.current,
      globalQuery: cleanSearch,
      page: 1,
    };
    tableParamsRef.current = updatedParams;
    setLoadingData(true);
    setTableParams(updatedParams);
    setFilterData(nextFilters);
  }, [quickSearch, tempFilterData]);

  const extraFiltersCount = useMemo(() => {
    let count = 0;
    if ((tempFilterData.DEPARTAMENTOS || []).length > 0) count++;
    if ((tempFilterData.PROVEEDORES || []).length > 0) count++;
    if ((tempFilterData.DIVISAS || []).length > 0) count++;
    return count;
  }, [tempFilterData]);

  const hasAnyActiveFilter = useMemo(() => {
    return Boolean(
      tempFilterData.ESTATUS ||
      (tempFilterData.EMPRESA_IDS && tempFilterData.EMPRESA_IDS.length > (empresaIds.length === 1 ? 1 : 0)) ||
      (tempFilterData.PROYECTOS && tempFilterData.PROYECTOS.length > 0) ||
      (tempFilterData.DIVISIONES && tempFilterData.DIVISIONES.length > 0) ||
      (tempFilterData.CENTRO_COSTOS && tempFilterData.CENTRO_COSTOS.length > 0) ||
      (tempFilterData.DEPARTAMENTOS && tempFilterData.DEPARTAMENTOS.length > 0) ||
      (tempFilterData.PROVEEDORES && tempFilterData.PROVEEDORES.length > 0) ||
      (tempFilterData.DIVISAS && tempFilterData.DIVISAS.length > 0) ||
      (quickSearch && quickSearch.trim() !== '')
    );
  }, [tempFilterData, empresaIds, quickSearch]);

  const resetFilters = useCallback(() => {
    setTempFilterData(baseFilters);
    setFilterData(baseFilters);
    setQuickSearch('');
    applyFilters(baseFilters, '');
  }, [applyFilters, baseFilters]);

  const renderMobileFooterMemo = useCallback(() => {
    return (
      <MobileTotalsFooter
        divisas={data?.totalesPorDivisa}
        totalCount={data?.total}
        onOpenPopover={(e, fieldKey) => {
          totalesPopoverRef.current?.open(e.currentTarget || e, fieldKey);
        }}
      />
    );
  }, [data?.totalesPorDivisa, data?.total]);

  const updateVisibleFilter = useCallback((field, value, options = {}) => {
    const { apply = true } = options;
    setTempFilterData(prev => {
      const updated = { ...prev, [field]: value };
      if (apply) {
        setLoadingData(true);
        setFilterData(updated);
        setTableParams(current => {
          const next = { ...current, page: 1 };
          tableParamsRef.current = next;
          return next;
        });
      }
      return updated;
    });
  }, []);

  const handleTableStateChange = useCallback((newState) => {
    // En SolicitudesProgramadas la búsqueda rápida se controla por quickSearch y el botón Buscar.
    // Solo actualizar globalQuery si la tabla envió explícitamente un texto de búsqueda no vacío;
    // de lo contrario conservar siempre la búsqueda actual.
    const currentGQ = tableParamsRef.current.globalQuery || '';
    const newGQ = (newState.globalQuery !== undefined && String(newState.globalQuery).trim() !== '')
      ? String(newState.globalQuery).trim()
      : currentGQ;
    const newSF = newState.sortConfig?.field || DEFAULT_SORT_FIELD;
    const newSD = newState.sortConfig?.direction || DEFAULT_SORT_DIR;
    const newPage = (newState.page ?? 0) + 1;
    const newPageSize = newState.rowsPerPage || 25;

    setTableParams(prev => {
      // Determinar si debemos resetear a la página 1 (si cambiaron filtros, orden o tamaño)
      const resetPage = prev.globalQuery !== newGQ || 
                        prev.sortField !== newSF || 
                        prev.sortDir !== newSD || 
                        prev.pageSize !== newPageSize;

      const nextRow = {
        page: resetPage ? 1 : newPage,
        pageSize: newPageSize,
        globalQuery: newGQ,
        sortField: newSF,
        sortDir: newSD,
        hiddenCols: newState.hiddenCols || []
      };

      // Solo actualizar si realmente hay un cambio para evitar bucles
      if (JSON.stringify(prev) === JSON.stringify(nextRow)) return prev;
      setLoadingData(true);
      tableParamsRef.current = nextRow;
      return nextRow;
    });
  }, []);

  const statusChipsMemo = useMemo(() => {
    if (esTesorero) {
      return statusChipsConfig.filter(item => item.label !== 'Default');
    }
    return statusChipsConfig;
  }, [esTesorero]);

  useEffect(() => {
    const cargarEmpresas = async () => {
      if (!apiUrl || !usuarioId) return;

      try {
        const response = await axios.get(`${apiUrl}/solicitudesAdmin/empresas/${usuarioId}`);
        const empresas = response.data?.empresas || [];
        const mapped = empresas
          .map(empresa => ({
            id: Number(empresa.EMPRESAID),
            nombre: empresa.NOMBRE_EMPRESA,
          }))
          .filter(empresa => empresaIds.includes(empresa.id));

        setEmpresaOptions(mapped);

        if (mapped.length === 1) {
          const yaTieneEmpresa = (filterData.EMPRESA_IDS || []).includes(mapped[0].id);
          if (!yaTieneEmpresa) {
            const updated = { ...tempFilterData, EMPRESA_IDS: [mapped[0].id], EMPRESA: mapped[0].nombre };
            setTempFilterData(prev => ({ ...prev, EMPRESA_IDS: [mapped[0].id], EMPRESA: mapped[0].nombre }));
            setFilterData(prev => ({ ...prev, EMPRESA_IDS: [mapped[0].id], EMPRESA: mapped[0].nombre }));
          }
        }
      } catch (err) {
        console.error('Error cargando empresas para historial:', err);
        const fallback = empresaIds.length === 1
          ? [{ id: empresaIds[0], nombre: nombreEmpresa || `Empresa ${empresaIds[0]}` }]
          : [];
        setEmpresaOptions(fallback);
      }
    };

    cargarEmpresas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, usuarioId, empresaScope]);

  const lastLoadedOpcionesEmpresasRef = useRef(null);
  const lastLoadedCatalogEmpresasRef = useRef(null);

  useEffect(() => {
    const cargarOpcionesFiltros = async () => {
      if (!apiUrl || !selectedCatalogEmpresaIds.length) {
        setFilterOptionRows([]);
        lastLoadedOpcionesEmpresasRef.current = null;
        return;
      }

      const empresasKey = `${selectedCatalogEmpresaIds.slice().sort().join(',')}_${tempFilterData.ESTATUS || ''}`;
      if (lastLoadedOpcionesEmpresasRef.current === empresasKey) return;
      lastLoadedOpcionesEmpresasRef.current = empresasKey;

      try {
        const response = await axios.post(`${apiUrl}/solicitudesProgramadas/opcionesFiltros`, {
          idEmpresa: selectedCatalogEmpresaIds,
          usuarioId,
          filterData: {
            ESTATUS: tempFilterData.ESTATUS || '',
          },
        });
        const rows = response.data?.rows || [];
        if (rows.length) {
          setFilterOptionRows(rows);
          return;
        }

        const departamentos = response.data?.departamentos || [];
        const centrosCosto = response.data?.centrosCosto || [];
        const proveedores = response.data?.proveedores || [];
        const divisas = response.data?.divisas || [];
        setFilterOptionRows([
          ...departamentos.map(DEPARTAMENTO => ({ DEPARTAMENTO })),
          ...centrosCosto.map(CENTROCOSTO => ({ CENTROCOSTO })),
          ...proveedores.map(PROVEEDOR => ({ PROVEEDOR })),
          ...divisas.map(DIVISA => ({ DIVISA })),
        ]);
      } catch (err) {
        console.error('Error cargando opciones de filtros:', err);
        setFilterOptionRows([]);
      }
    };

    cargarOpcionesFiltros();
  }, [apiUrl, selectedCatalogEmpresaIds, tempFilterData.ESTATUS, usuarioId]);

  // Cargar Catálogos de Proyectos, Divisiones y Centros de Costo (acotados a los permisos del usuario)
  useEffect(() => {
    if (!apiUrl || !selectedCatalogEmpresaIds.length) return;

    const empresasKey = selectedCatalogEmpresaIds.slice().sort().join(',');
    if (lastLoadedCatalogEmpresasRef.current === empresasKey) return;
    lastLoadedCatalogEmpresasRef.current = empresasKey;

    axios.post(`${apiUrl}/proyectosTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setProyectosCatalogo(raw.filter(p => Boolean(p.ACTIVO)));
      })
      .catch(() => setProyectosCatalogo([]));

    axios.post(`${apiUrl}/divisionesTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setDivisionesCatalogo(raw.filter(d => Boolean(d.ACTIVO)));
      })
      .catch(() => setDivisionesCatalogo([]));

    axios.post(`${apiUrl}/centroCostosTabla`, { idEmpresa: selectedCatalogEmpresaIds, usuarioId, idPerfil })
      .then(res => {
        const raw = res.data?.contenido || (Array.isArray(res.data) ? res.data : []);
        setCentrosCostoCatalogo(raw.filter(c => Boolean(c.ACTIVO)));
      })
      .catch(() => setCentrosCostoCatalogo([]));
  }, [apiUrl, selectedCatalogEmpresaIds, usuarioId, idPerfil]);

  // ─── Opciones Dinámicas en Cascada Jerárquica ───
  // 1. Proyectos disponibles (filtrados por los centros de costo a los que el usuario tiene acceso)
  const proyectoOptions = useMemo(() => {
    // Extraer qué proyectos tienen al menos 1 centro de costo accesible por el usuario
    const allowedProyIds = new Set(
      centrosCostoCatalogo.map(c => Number(c.ID_PROYECTO)).filter(id => id > 0)
    );

    const map = new Map();
    proyectosCatalogo.forEach(p => {
      const pid = Number(p.ID_PROYECTO);
      // Si hay CCs cargados, filtrar por los permitidos
      if (centrosCostoCatalogo.length === 0 || allowedProyIds.has(pid)) {
        if (p.ID_PROYECTO && p.NOMBRE_PROYECTO) {
          map.set(pid, p.NOMBRE_PROYECTO);
        }
      }
    });
    const list = Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
    list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return list;
  }, [proyectosCatalogo, centrosCostoCatalogo]);

  // 2. Grupos / Divisiones (filtrados por los proyectos seleccionados y por los CCs permitidos del usuario)
  const divisionOptions = useMemo(() => {
    const selectedProyIds = (tempFilterData.PROYECTOS || []).map(Number);
    
    // Extraer qué divisiones tienen al menos 1 centro de costo accesible por el usuario
    const allowedDivIds = new Set(
      centrosCostoCatalogo.map(c => Number(c.ID_DIVISION)).filter(id => id > 0)
    );

    const map = new Map();
    divisionesCatalogo.forEach(d => {
      const pId = Number(d.ID_PROYECTO);
      const dId = Number(d.ID_DIVISION);
      
      const matchProy = !selectedProyIds.length || selectedProyIds.includes(pId);
      const matchAllowed = centrosCostoCatalogo.length === 0 || allowedDivIds.has(dId);

      if (matchProy && matchAllowed) {
        if (d.ID_DIVISION && d.NOMBRE_DIVISION) {
          map.set(dId, d.NOMBRE_DIVISION);
        }
      }
    });
    const list = Array.from(map.entries()).map(([id, nombre]) => ({ id, nombre }));
    list.sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));
    return list;
  }, [divisionesCatalogo, tempFilterData.PROYECTOS, centrosCostoCatalogo]);

  // Resumen visual estilo Breadcrumbs: Empresa > Proyecto > Grupo > CC
  const hierarchicalSummary = useMemo(() => {
    const parts = [];
    const empCount = (tempFilterData.EMPRESA_IDS || []).length;
    if (empCount === 1) {
      const emp = empresaOptions.find(e => e.id === tempFilterData.EMPRESA_IDS[0]);
      if (emp) parts.push(emp.nombre);
    } else if (empCount > 1) {
      parts.push(`${empCount} Emp`);
    }

    const proyCount = (tempFilterData.PROYECTOS || []).length;
    if (proyCount === 1) {
      const proy = proyectoOptions.find(p => p.id === tempFilterData.PROYECTOS[0]);
      if (proy) parts.push(proy.nombre);
    } else if (proyCount > 1) {
      parts.push(`${proyCount} Proy`);
    }

    const divCount = (tempFilterData.DIVISIONES || []).length;
    if (divCount === 1) {
      const div = divisionOptions.find(d => d.id === tempFilterData.DIVISIONES[0]);
      if (div) parts.push(div.nombre);
    } else if (divCount > 1) {
      parts.push(`${divCount} Grupos`);
    }

    const ccCount = (tempFilterData.CENTRO_COSTOS || []).length;
    if (ccCount === 1) {
      parts.push(tempFilterData.CENTRO_COSTOS[0].split(' - ')[0] || tempFilterData.CENTRO_COSTOS[0]);
    } else if (ccCount > 1) {
      parts.push(`${ccCount} CCs`);
    }

    if (!parts.length) return 'Empresa / Proyecto / Grupo / CC';
    if (empCount > 0 && !proyCount && !divCount && !ccCount) {
      return `${parts[0]} › Proyecto / Grupo / CC`;
    }
    return parts.join(' › ');
  }, [tempFilterData.EMPRESA_IDS, tempFilterData.PROYECTOS, tempFilterData.DIVISIONES, tempFilterData.CENTRO_COSTOS, empresaOptions, proyectoOptions, divisionOptions]);

  const hasHierarchicalFilter = useMemo(() => {
    return (tempFilterData.EMPRESA_IDS || []).length > 0 ||
      (tempFilterData.PROYECTOS || []).length > 0 ||
      (tempFilterData.DIVISIONES || []).length > 0 ||
      (tempFilterData.CENTRO_COSTOS || []).length > 0;
  }, [tempFilterData.EMPRESA_IDS, tempFilterData.PROYECTOS, tempFilterData.DIVISIONES, tempFilterData.CENTRO_COSTOS]);

  const optionSourceRows = useMemo(() => (
    filterOptionRows.length ? filterOptionRows : (Array.isArray(data?.contenido) ? data.contenido : [])
  ), [data?.contenido, filterOptionRows]);

  const getOptionRows = useCallback((excludeField = '') => {
    const selectedDepartamentos = tempFilterData.DEPARTAMENTOS || [];
    const selectedCentros = getCentroCostoFilterValues(tempFilterData.CENTRO_COSTOS || []);
    const selectedProveedores = tempFilterData.PROVEEDORES || [];
    const selectedDivisas = tempFilterData.DIVISAS || [];

    return optionSourceRows.filter(row => {
      const centroNombre = getCentroCostoFilterValue(buildCentroCostoOption(row));

      return (
        (excludeField === 'DEPARTAMENTOS' || matchesAnyOption(row.DEPARTAMENTO, selectedDepartamentos)) &&
        (excludeField === 'CENTRO_COSTOS' || matchesAnyOption(centroNombre, selectedCentros)) &&
        (excludeField === 'PROVEEDORES' || matchesAnyOption(row.PROVEEDOR, selectedProveedores)) &&
        (excludeField === 'DIVISAS' || matchesAnyOption(row.DIVISA, selectedDivisas))
      );
    });
  }, [optionSourceRows, tempFilterData]);

  // 3. Centros de Costo en Cascada
  const centroCostoOptions = useMemo(() => {
    const selectedProyIds = (tempFilterData.PROYECTOS || []).map(Number);
    const selectedDivIds = (tempFilterData.DIVISIONES || []).map(Number);

    // Si hay catálogo de CC cargado, filtrar según proyecto y división
    if (centrosCostoCatalogo.length) {
      const filtered = centrosCostoCatalogo.filter(cc => {
        const pId = Number(cc.ID_PROYECTO);
        const dId = Number(cc.ID_DIVISION);

        if (selectedDivIds.length && !selectedDivIds.includes(dId)) return false;
        if (selectedProyIds.length && !selectedProyIds.includes(pId)) return false;
        return true;
      });

      const options = filtered.map(buildCentroCostoOption);
      return getUniqueOptions(options);
    }

    return getUniqueOptions(getOptionRows('CENTRO_COSTOS').map(buildCentroCostoOption));
  }, [centrosCostoCatalogo, tempFilterData.PROYECTOS, tempFilterData.DIVISIONES, getOptionRows]);

  const departamentoOptions = useMemo(() => (
    getUniqueOptions(getOptionRows('DEPARTAMENTOS').map(row => row.DEPARTAMENTO))
  ), [getOptionRows]);

  const proveedorOptions = useMemo(() => (
    getUniqueOptions(getOptionRows('PROVEEDORES').map(row => row.PROVEEDOR))
  ), [getOptionRows]);

  const divisaOptions = useMemo(() => (
    getUniqueOptions(getOptionRows('DIVISAS').map(row => row.DIVISA))
  ), [getOptionRows]);

  const abortRef = useRef(null);

  const resolveCentroCostosFilter = useCallback(() => {
    const hasProyectosFilter = (filterData.PROYECTOS || []).length > 0;
    const hasDivisionesFilter = (filterData.DIVISIONES || []).length > 0;
    const hasCentrosFilter = (filterData.CENTRO_COSTOS || []).length > 0;

    if (hasCentrosFilter) {
      return getCentroCostoFilterValues(filterData.CENTRO_COSTOS);
    }
    if (hasDivisionesFilter || hasProyectosFilter) {
      const divIds = (filterData.DIVISIONES || []).map(Number);
      const proyIds = (filterData.PROYECTOS || []).map(Number);
      const ccs = centrosCostoCatalogo.filter(c => {
        const matchDiv = !divIds.length || divIds.includes(Number(c.ID_DIVISION));
        const matchProy = !proyIds.length || proyIds.includes(Number(c.ID_PROYECTO));
        const matchEmp = !selectedEmpresaIds.length || !c.IDEMPRESA || selectedEmpresaIds.includes(Number(c.IDEMPRESA));
        return matchDiv && matchProy && matchEmp;
      });
      const resolved = Array.from(new Set(ccs.flatMap(c => [
        String(c.NUMERO_CC || c.NUMERO || '').trim(),
        String(c.CENTRO_COSTO || c.NOMBRE || '').trim()
      ]).filter(Boolean)));
      return resolved.length ? resolved : ['__NO_MATCH__'];
    }
    return filterData.CENTRO_COSTO;
  }, [filterData.CENTRO_COSTOS, filterData.DIVISIONES, filterData.PROYECTOS, filterData.CENTRO_COSTO, centrosCostoCatalogo, selectedEmpresaIds]);

  const fetchData = useCallback(async () => {
    if (!apiUrl || !loggedInUsername || !selectedEmpresaIds.length || !idPerfil) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoadingData(true);
    try {
      const { page, pageSize, globalQuery, sortField, sortDir } = tableParams;
      const effectiveSortField = sortField || DEFAULT_SORT_FIELD;
      const effectiveSortDir = sortDir || DEFAULT_SORT_DIR;
      const shouldIgnoreDates = Boolean(String(globalQuery || '').trim());
      // Resolver lista de Centros de Costo según el nivel seleccionado (CC puntual > Grupo > Proyecto)
      const resolvedCentroCostos = resolveCentroCostosFilter();

      const serverFilterData = {
        ...filterData,
        FECHA_INICIO: shouldIgnoreDates ? '' : filterData.FECHA_INICIO,
        FECHA_FIN: shouldIgnoreDates ? '' : filterData.FECHA_FIN,
        PROVEEDOR: (filterData.PROVEEDORES || []).length ? filterData.PROVEEDORES : filterData.PROVEEDOR,
        CENTRO_COSTO: resolvedCentroCostos,
        DEPARTAMENTO: (filterData.DEPARTAMENTOS || []).length ? filterData.DEPARTAMENTOS : filterData.DEPARTAMENTO,
        DIVISA: (filterData.DIVISAS || []).length ? filterData.DIVISAS : filterData.DIVISA,
        EMPRESA: (selectedEmpresaIds.length === 1 && filterData.EMPRESA && !filterData.EMPRESA.includes(',')) ? filterData.EMPRESA : '',
      };
      delete serverFilterData.EMPRESA_IDS;
      delete serverFilterData.PROVEEDORES;
      delete serverFilterData.DIVISAS;
      delete serverFilterData.CENTRO_COSTOS;
      delete serverFilterData.DEPARTAMENTOS;
      delete serverFilterData.PROYECTO;
      delete serverFilterData.PROYECTOS;
      delete serverFilterData.DIVISION;
      delete serverFilterData.DIVISIONES;

      const combinedFilter = {
        ...buildFilterPayload(serverFilterData),
        globalQuery: globalQuery || '',
        sortField: effectiveSortField,
        sortDir: effectiveSortDir,
        page,
        pageSize
      };

      const response = await axios.post(
        `${apiUrl}/solicitudesProgramadasTabla1`,
        {
          idEmpresa: selectedEmpresaIds,
          idPerfil,
          usuarioId,
          nombreEmpresa: selectedEmpresaIds.length === 1 && !nombreEmpresa?.toUpperCase().includes('TODA') && !nombreEmpresa?.toUpperCase().includes('ALL') ? nombreEmpresa : null,
          loggedInUsername,
          filterData: combinedFilter,
        },
        { signal: controller.signal }
      );

      setData(response.data || { contenido: [] });
      setError(null);
    } catch (err) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
      console.error('Error al cargar los datos:', err);
      setData({ contenido: [] });
      setError('No se pudieron cargar los datos. Intenta más tarde.');
      handleOpenSweetAlert('No se pudieron cargar los datos.', 'error');
    } finally {
      if (abortRef.current === controller) {
        setLoadingData(false);
        abortRef.current = null;
      }
    }
  }, [apiUrl, loggedInUsername, usuarioId, selectedEmpresaIds, idPerfil, nombreEmpresa, filterData, tableParams, centrosCostoCatalogo, resolveCentroCostosFilter]);

  const emptyStateData = useMemo(() => {
    const activeFiltersList = [];

    const formatFmt = (strDate) => {
      if (!strDate) return '';
      const parts = strDate.split('-');
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : strDate;
    };

    const dateRangeStr = (filterData.FECHA_INICIO && filterData.FECHA_FIN)
      ? `${formatFmt(filterData.FECHA_INICIO)} al ${formatFmt(filterData.FECHA_FIN)}`
      : '';

    if (filterData.FOLIO) activeFiltersList.push(`Folio/Requisición: "${filterData.FOLIO}"`);

    if (Array.isArray(filterData.DEPARTAMENTOS) && filterData.DEPARTAMENTOS.length > 0) {
      activeFiltersList.push(`Departamento: ${filterData.DEPARTAMENTOS.join(', ')}`);
    } else if (filterData.DEPARTAMENTO) {
      activeFiltersList.push(`Departamento: ${filterData.DEPARTAMENTO}`);
    }

    if (Array.isArray(filterData.CENTRO_COSTOS) && filterData.CENTRO_COSTOS.length > 0) {
      activeFiltersList.push(`Centro de Costo: ${filterData.CENTRO_COSTOS.join(', ')}`);
    } else if (filterData.CENTRO_COSTO) {
      activeFiltersList.push(`Centro de Costo: ${filterData.CENTRO_COSTO}`);
    }

    if (Array.isArray(filterData.PROVEEDORES) && filterData.PROVEEDORES.length > 0) {
      activeFiltersList.push(`Proveedor: ${filterData.PROVEEDORES.join(', ')}`);
    } else if (filterData.PROVEEDOR) {
      activeFiltersList.push(`Proveedor: ${filterData.PROVEEDOR}`);
    }

    if (Array.isArray(filterData.DIVISAS) && filterData.DIVISAS.length > 0) {
      activeFiltersList.push(`Divisa: ${filterData.DIVISAS.join(', ')}`);
    } else if (filterData.DIVISA) {
      activeFiltersList.push(`Divisa: ${filterData.DIVISA}`);
    }

    if (filterData.ESTATUS) {
      activeFiltersList.push(`Estatus: ${filterData.ESTATUS}`);
    }

    if (tableParams.globalQuery) {
      activeFiltersList.push(`Búsqueda rápida: "${tableParams.globalQuery}"`);
    }

    return {
      dateRangeStr,
      activeFiltersList
    };
  }, [filterData, tableParams.globalQuery]);

  useEffect(() => {
    fetchData();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchData]);

  const handleOpenAuthModal = useCallback((row) => {
    setSelectedRow(row);
    setOpenAuthModal(true);
  }, []);



  const handleViewFile = useCallback(async (row) => {
    setLoadingAction(true);
    try {
      const response = await axios.post(`${apiUrl}/pagosAutDescargar`, { id: row.ID });
      const { adjuntos = [] } = response.data || {};

      let comprobantesAdjuntos = [];
      try {
        const idSolicitud = row.ID_SOLICITUD || row.ID;
        if (idSolicitud) {
          const compResponse = await axios.post(`${apiUrl}/comprobanteAutDescargar`, { id: idSolicitud });
          comprobantesAdjuntos = compResponse.data?.adjuntos || [];
        }
      } catch (err) {
        console.warn('No se pudieron cargar comprobantes adicionales', err);
      }

      if (!adjuntos.length && !comprobantesAdjuntos.length) {
        handleOpenSweetAlert('No hay archivos adjuntos.', 'info');
        return;
      }

      setInitialFileId(null);

      const files = adjuntos.map((file) => {
        const ext = file.filename.split('.').pop().toLowerCase();
        return {
          id: file.archivo_id,
          filename: file.filename,
          previewUrl: `${apiUrl}/visualizarPagosAut/${file.archivo_id}`,
          downloadUrl: `${apiUrl}/descargarPagosAut/${file.archivo_id}`,
          fileType: getMimeTypeFromExtension(ext),
          category: file.category || deriveCategory(file.filepath || ''),
        };
      });

      const officeExts = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
      const compFiles = comprobantesAdjuntos.map((file) => {
        const filename = file.FILENAME;
        const fileId = file.ID_REF;
        const ext = filename.split('.').pop().toLowerCase();
        const isOffice = officeExts.includes(ext);
        const origen = String(file.ORIGEN || '').toUpperCase();
        const rawId = String(fileId || '');

        let targetId = rawId;
        if (!rawId.startsWith('COMP_') && !rawId.startsWith('TESO_')) {
          if (origen === 'PROGRAMADAS') {
            targetId = `TESO_ADJ_${rawId}`;
          } else if (origen === 'NUEVO') {
            targetId = `COMP_NEW_${rawId}`;
          } else if (origen === 'VIEJO') {
            targetId = `COMP_OLD_${rawId}`;
          } else {
            targetId = `COMP_PAGO_${rawId}`;
          }
        }

        return {
          id: `COMP_${fileId}_${filename}`,
          filename,
          previewUrl: `${apiUrl}/visualizarPagosAut/${targetId}`,
          downloadUrl: `${apiUrl}/descargarPagosAut/${targetId}`,
          fileType: isOffice ? 'application/pdf' : getMimeTypeFromExtension(ext),
          category: 'Comprobante de pago',
        };
      });

      setFileList([...files, ...compFiles]);
      setFileModalOpen(true);
    } catch (error) {
      console.error('Error al obtener archivos adjuntos:', error);
      handleOpenSweetAlert('Error al obtener archivos adjuntos', 'error');
    } finally {
      setLoadingAction(false);
    }
  }, [apiUrl]);

  const handleViewSpecificFile = useCallback(async (row, targetFileId) => {
    if (!targetFileId) return;
    setLoadingAction(true);
    try {
      const response = await axios.post(`${apiUrl}/pagosAutDescargar`, { id: row.ID });
      const { adjuntos = [] } = response.data || {};

      let targetIds = [targetFileId];
      if (typeof targetFileId === 'string' && targetFileId.startsWith('[')) {
        try {
          targetIds = JSON.parse(targetFileId);
        } catch (e) {
          targetIds = [targetFileId];
        }
      }

      const filteredAdjuntos = adjuntos.filter((a) => {
        const isTarget = targetIds.some(id => String(id) === String(a.archivo_id));
        const isOtros = deriveCategory(a.filepath || '') === 'OTROS';
        return isTarget || isOtros;
      });

      if (!filteredAdjuntos.length) {
        handleOpenSweetAlert('No se encontraron archivos para mostrar.', 'warning');
        return;
      }

      setInitialFileId(targetIds);


      const files = filteredAdjuntos.map((file) => {
        const ext = file.filename.split('.').pop().toLowerCase();
        return {
          id: file.archivo_id,
          filename: file.filename,
          previewUrl: `${apiUrl}/visualizarPagosAut/${file.archivo_id}`,
          downloadUrl: `${apiUrl}/descargarPagosAut/${file.archivo_id}`,
          fileType: getMimeTypeFromExtension(ext),
          category: file.category || deriveCategory(file.filepath || ''),
        };
      });

      setFileList(files);
      setFileModalOpen(true);
    } catch (error) {
      console.error('Error al obtener archivo específico:', error);
      handleOpenSweetAlert('Error al obtener el archivo.', 'error');
    } finally {
      setLoadingAction(false);
    }
  }, [apiUrl]);

  const handleViewPagoFile = useCallback(async (row) => {
    setLoadingAction(true);
    try {
      const idSolicitud = row.ID_SOLICITUD || row.ID;

      if (!idSolicitud) {
        console.error('No viene ID_SOLICITUD ni ID en la fila:', row);
        handleOpenSweetAlert('No se encontró el ID de la solicitud para el comprobante.', 'error');
        return;
      }

      const response = await axios.post(`${apiUrl}/comprobanteAutDescargar`, { id: idSolicitud });
      const { adjuntos = [] } = response.data || {};

      if (!adjuntos.length) {
        handleOpenSweetAlert(buildNoAdjuntoMessage(row), 'info');
        return;
      }

      const officeExts = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];

      const files = adjuntos.map((file) => {
        const filename = file.FILENAME;
        const fileId = file.ID_REF;
        const ext = filename.split('.').pop().toLowerCase();
        const isOffice = officeExts.includes(ext);
        const origen = String(file.ORIGEN || '').toUpperCase();
        const rawId = String(fileId || '');

        let targetId = rawId;
        if (!rawId.startsWith('COMP_') && !rawId.startsWith('TESO_')) {
          if (origen === 'PROGRAMADAS') {
            targetId = `TESO_ADJ_${rawId}`;
          } else if (origen === 'NUEVO') {
            targetId = `COMP_NEW_${rawId}`;
          } else if (origen === 'VIEJO') {
            targetId = `COMP_OLD_${rawId}`;
          } else {
            targetId = `COMP_PAGO_${rawId}`;
          }
        }

        return {
          id: fileId,
          filename,
          previewUrl: `${apiUrl}/visualizarPagosAut/${targetId}`,
          downloadUrl: `${apiUrl}/descargarPagosAut/${targetId}`,
          fileType: isOffice ? 'application/pdf' : getMimeTypeFromExtension(ext),
          origen: file.ORIGEN,
          category: 'COMPROBANTE',
        };
      });

      setFileList(files);
      setFileModalOpen(true);
    } catch (error) {
      console.error('Error al obtener comprobante de pago:', error);
      handleOpenSweetAlert('Error al obtener comprobante de pago', 'error');
    } finally {
      setLoadingAction(false);
    }
  }, [apiUrl]);



  const buildRowExport = useCallback((row) => {
    const rowPartidas = Array.isArray(row?.partidas) ? row.partidas : [];
    const descFormatted = rowPartidas.length > 0
      ? rowPartidas
          .map((p) => `- ${p.DESCRIPCION_PART || p.DESCRIPCION || p.CONCEPTO_PROYECTO || 'S/D'}${p.CANTIDAD ? ` (Ct: ${p.CANTIDAD})` : ''}`)
          .join('\n')
      : (row?.DESCRIPCION || row?.DESCRIPCION_PART || row?.CONCEPTO_PROYECTO || row?.NOTAS_OBSERVACIONES || row?.NOTAS || '');

    return {
      ...row,
      DESCRIPCION: descFormatted,
      DESCRIPCION_PART: descFormatted,
      ...buildFechasAutExport(row),
    };
  }, []);

  const dataForExport = useMemo(() => {
    const contenido = Array.isArray(data?.contenido) ? data.contenido : [];

    return {
      ...data,
      contenido: contenido.map(buildRowExport),
    };
  }, [data, buildRowExport]);

  // ✅ Dinámico solo para Excel/exportación
  const dataForTable = useMemo(() => {
    const contenido = Array.isArray(data?.contenido) ? data.contenido : [];

    return {
      ...data,
      contenido: contenido.map((row) => {
        const compactRow = {
          ...row,
          OC_REQUI: '',
          FECHA_SOLICITANTE: '',
          PROVEEDOR_COMPRADOR: '',
          CENTRO_DEPTO: '',
          TOTAL_DIVISA: '',
          MONTO_RESTANTE: '',
          EXHIBIT: '',
          FACTURA_COMPACTA: '',
          ESTATUS_COMPACTO: row?.ESTATUS || '',
        };

        return compactRow;
      }),
    };
  }, [data]);

  // ── Render Footer Row Memoizado: lee de dataTotalesRef y dataTotalCountRef ──
  const renderFooterRowMemo = useCallback(({ orderedKeys, stickyLeftMeta, hasActions, selectable, hasPartidas }) => {
    const totalesPorDivisa = dataTotalesRef.current;
    if (!totalesPorDivisa || Object.keys(totalesPorDivisa).length === 0) return null;

    let countTotalGeneral = 0;
    Object.values(totalesPorDivisa).forEach(v => {
      countTotalGeneral += (v.count || 0);
    });

    const displayTotalCount = dataTotalCountRef.current || countTotalGeneral;

    const handleOpenPopover = (e, fieldKey) => {
      totalesPopoverRef.current?.open(e.currentTarget, fieldKey);
    };

    const fmt = (n) => (n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const getBadgeStyle = (div) => {
      if (div === 'USD') return { color: '#b45309', bg: '#fef3c7' };
      if (div === 'EUR') return { color: '#4338ca', bg: '#e0e7ff' };
      if (div === 'MXN') return { color: '#0369a1', bg: '#e0f2fe' };
      if (div === 'GBP') return { color: '#701a75', bg: '#fae8ff' };
      if (div === 'CAD') return { color: '#991b1b', bg: '#fee2e2' };
      return { color: '#334155', bg: '#f1f5f9' };
    };

    const getCurrencySymbol = (div) => {
      if (div === 'EUR') return '€';
      if (div === 'GBP') return '£';
      return '$';
    };

    const divisasEntries = Object.entries(totalesPorDivisa || {}).sort((a, b) => {
      const order = { MXN: 1, USD: 2, EUR: 3 };
      return (order[a[0]] || 99) - (order[b[0]] || 99);
    });

    return (
      <React.Fragment>
        <tfoot style={{ position: 'sticky', bottom: 0, zIndex: 12, backgroundColor: '#f8fafc' }}>
          <tr style={{ backgroundColor: '#f8fafc', borderTop: '2px solid #cbd5e1', borderBottom: '1px solid #cbd5e1' }}>
            {selectable && <td style={{ backgroundColor: '#f8fafc', padding: '2px 4px' }} />}
            {hasPartidas && <td style={{ backgroundColor: '#f8fafc', padding: '2px 4px' }} />}
            
            {orderedKeys.map((key) => {
              const isSticky = stickyLeftMeta && stickyLeftMeta[key];
              const stickyStyle = isSticky ? {
                position: 'sticky',
                left: isSticky.left,
                zIndex: 13,
                backgroundColor: '#f8fafc',
                boxShadow: isSticky.isLastSticky ? '3px 0 6px -2px rgba(0, 0, 0, 0.12)' : 'none',
              } : {};

              const isFirstCol = key === orderedKeys[0];
              let content = null;
              let isClickable = false;
              let fieldKey = null;

              if (isFirstCol) {
                content = (
                  <Typography sx={{ fontWeight: 850, fontSize: '0.60rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    TOTALES ({displayTotalCount})
                  </Typography>
                );
              } else if (key === 'TOTAL' || key === 'TOTAL_DIVISA' || key === 'MONTOSOLICITADO') {
                isClickable = true;
                fieldKey = 'TOTAL';
                content = (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5px', py: 0.1 }}>
                    {divisasEntries.map(([divisa, vals]) => {
                      const total = vals.total || 0;
                      const sol = vals.solicitado || 0;
                      const rest = total > sol ? total - sol : (vals.restante || 0);
                      const { color: badgeColor, bg: badgeBg } = getBadgeStyle(divisa);
                      const sym = getCurrencySymbol(divisa);
                      return (
                        <Box key={divisa} sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <Typography sx={{ fontSize: '0.50rem', fontWeight: 800, color: badgeColor, bgcolor: badgeBg, px: 0.35, py: 0.05, borderRadius: '2px', minWidth: 20, textAlign: 'center', lineHeight: 1.1 }}>
                              {divisa}
                            </Typography>
                            <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: total > 0 ? '#0f172a' : '#94a3b8', fontVariantNumeric: 'tabular-nums', lineHeight: 1, whiteSpace: 'nowrap' }}>
                              {sym}{fmt(total)}
                            </Typography>
                          </Box>
                          {rest > 0 && (
                            <Typography sx={{ fontSize: '0.48rem', visibility: 'hidden', lineHeight: 1, mt: '0.5px' }}>
                              -
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              } else if (key === 'SOLICITADO' || key === 'MONTOSOLICITADO_REST' || key === 'SOLICITADO_REST' || key === 'MONTO_RESTANTE') {
                isClickable = true;
                fieldKey = 'SOLICITADO';
                content = (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.5px', py: 0.1 }}>
                    {divisasEntries.map(([divisa, vals]) => {
                      const total = vals.total || 0;
                      const sol = vals.solicitado || 0;
                      const rest = total > sol ? total - sol : (vals.restante || 0);
                      const { color: badgeColor, bg: badgeBg } = getBadgeStyle(divisa);
                      const sym = getCurrencySymbol(divisa);
                      return (
                        <Box key={divisa} sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                            <Typography sx={{ fontSize: '0.50rem', fontWeight: 800, color: badgeColor, bgcolor: badgeBg, px: 0.35, py: 0.05, borderRadius: '2px', minWidth: 20, textAlign: 'center', lineHeight: 1.1 }}>
                              {divisa}
                            </Typography>
                            <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: sol > 0 ? '#16a34a' : '#94a3b8', fontVariantNumeric: 'tabular-nums', lineHeight: 1, whiteSpace: 'nowrap' }}>
                              {sym}{fmt(sol)}
                            </Typography>
                          </Box>
                          {rest > 0 && (
                            <Typography sx={{ fontSize: '0.50rem', fontWeight: 750, color: '#ea580c', fontVariantNumeric: 'tabular-nums', pl: '24px', mt: '0.5px', lineHeight: 1, whiteSpace: 'nowrap' }}>
                              Rest: {sym}{fmt(rest)}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                );
              }

              return (
                <td
                  key={key}
                  onClick={(e) => isClickable && handleOpenPopover(e, fieldKey)}
                  style={{
                    ...stickyStyle,
                    padding: '3px 8px',
                    backgroundColor: isClickable ? '#f1f5f9' : '#f8fafc',
                    cursor: isClickable ? 'pointer' : 'default',
                    transition: 'background-color 0.15s',
                    verticalAlign: 'middle',
                  }}
                >
                  {content}
                </td>
              );
            })}

            {hasActions && <td style={{ backgroundColor: '#f8fafc', padding: '3px 6px' }} />}
          </tr>
        </tfoot>
      </React.Fragment>
    );
  }, []);

  const exportLabelsExtended = useMemo(() => {
    const contenido = Array.isArray(dataForExport?.contenido) ? dataForExport.contenido : [];

    let maxAut = 0;
    let maxTeso = 0;

    for (const r of contenido) {
      for (const k of Object.keys(r || {})) {
        const m1 = k.match(/^FECHA_AUT_N(\d+)$/);
        if (m1) maxAut = Math.max(maxAut, Number(m1[1]));

        const m2 = k.match(/^FECHA_TESO_N(\d+)$/);
        if (m2) maxTeso = Math.max(maxTeso, Number(m2[1]));
      }
    }

    const hidden = tableParams.hiddenCols || [];
    const labels = {
      ...exportLabels,
      FECHA_AUT_ULTIMA: 'Última fecha autorización',
    };

    for (let i = 1; i <= maxAut; i++) {
      labels[`FECHA_AUT_N${i}`] = `Fecha autorización N${i}`;
    }

    for (let i = 1; i <= maxTeso; i++) {
      labels[`FECHA_TESO_N${i}`] = `Fecha tesorería N${i}`;
    }

    // Filtrar columnas ocultas dinámicamente
    const finalLabels = {};
    const hiddenSet = new Set(hidden);
    
    Object.keys(labels).forEach(key => {
      if (!hiddenSet.has(key)) {
finalLabels[key] = labels[key];
      }
    });

    return finalLabels;
  }, [dataForExport, tableParams.hiddenCols]);

  const handleExportFetchAll = useCallback(async ({ scope = 'filters', fromDate = '', toDate = '' } = {}) => {
    if (!apiUrl || !loggedInUsername || !empresaIds.length || !idPerfil) return dataForExport;

    try {
      let exportFilterData;

      if (scope === 'all') {
        exportFilterData = {};
      } else if (scope === 'dateRange') {
        exportFilterData = {
          FECHA_INICIO: fromDate || null,
          FECHA_FIN: toDate || null,
        };
      } else {
        const currentGQ = tableParams.globalQuery || '';
        const shouldIgnoreDates = Boolean(String(filterData?.FOLIO || '').trim());
        const resolvedCentroCostos = resolveCentroCostosFilter();
        const serverFilterData = {
          ...filterData,
          FECHA_INICIO: shouldIgnoreDates ? '' : filterData.FECHA_INICIO,
          FECHA_FIN: shouldIgnoreDates ? '' : filterData.FECHA_FIN,
          PROVEEDOR: (filterData.PROVEEDORES || []).length ? filterData.PROVEEDORES : filterData.PROVEEDOR,
          CENTRO_COSTO: resolvedCentroCostos,
          DEPARTAMENTO: (filterData.DEPARTAMENTOS || []).length ? filterData.DEPARTAMENTOS : filterData.DEPARTAMENTO,
          DIVISA: (filterData.DIVISAS || []).length ? filterData.DIVISAS : filterData.DIVISA,
        };

        delete serverFilterData.EMPRESA_IDS;
        delete serverFilterData.PROVEEDORES;
        delete serverFilterData.DIVISAS;
        delete serverFilterData.CENTRO_COSTOS;
        delete serverFilterData.DEPARTAMENTOS;
        delete serverFilterData.PROYECTO;
        delete serverFilterData.PROYECTOS;
        delete serverFilterData.DIVISION;
        delete serverFilterData.DIVISIONES;

        exportFilterData = {
          ...buildFilterPayload(serverFilterData),
          globalQuery: currentGQ,
        };
      }

      const combinedFilter = {
        ...exportFilterData,
        sortField: tableParams.sortField || DEFAULT_SORT_FIELD,
        sortDir: tableParams.sortDir || DEFAULT_SORT_DIR,
        page: 1,
        pageSize: Math.max(data?.total || 5000, 5000)
      };

      const response = await axios.post(
        `${apiUrl}/solicitudesProgramadasTabla1`,
        {
          idEmpresa: empresaIds,
          idPerfil,
          usuarioId,
          nombreEmpresa: empresaIds.length === 1 && !nombreEmpresa?.toUpperCase().includes('TODA') && !nombreEmpresa?.toUpperCase().includes('ALL') ? nombreEmpresa : null,
          loggedInUsername,
          filterData: combinedFilter,
        }
      );

      const contenido = Array.isArray(response.data?.contenido) ? response.data.contenido : [];

      return {
        ...response.data,
        contenido: contenido.map(buildRowExport),
      };
    } catch (err) {
      console.error('Error fetching all data for export:', err);
      return dataForExport;
    }
  }, [apiUrl, loggedInUsername, usuarioId, empresaIds, idPerfil, nombreEmpresa, filterData, tableParams, data?.total, dataForExport, resolveCentroCostosFilter, buildRowExport]);

  const handleCancelarAutorizacion = useCallback((row) => {
    setModalStates((prev) => ({
      ...prev,
      observaciones: '',
      cancelarID: row.ID,
      cancelarEmpresa: row.EMPRESA,
      diasRestantes: row.DIASRESTANTES ?? 0,
      folio: row.FOLIO || row.ID || 'N/D',
      proveedor: row.PROVEEDOR || 'N/D',
      fechaSolicitud: row.FECHASOLICITUD || row.FECHA || 'N/D',
      monto: row.MONTOSOLICITADO || row.MONTO || 'N/D',
      departamento: row.DEPARTAMENTO || 'N/D',
      centroCostos: row.CENTROCOSTO || row.NUMEROCC || 'N/D',
      fechaVencimiento: row.FECHA_VENCIMIENTO_REAL ?? row.VENCIMIENTO ?? 'N/D',
      openCancelacionModal: true,
    }));
  }, []);

  const cancelarSolicitud = useCallback(async (correctionData) => {
    setModalStates((prev) => ({
      ...prev,
      openCancelacionModal: false,
    }));

    setLoadingAction(true);

    try {
      await cancelarPago({
        ID: modalStates.cancelarID,
        EMPRESA: modalStates.cancelarEmpresa,
        OBSERVACIONES: correctionData.detalle,
        USUARIO: loggedInUsername,
        MOTIVO: correctionData.motivo,
        DETALLE: correctionData.detalle,
        SECCIONES: correctionData.secciones,
        RECLASIFICAR: correctionData.reclasificar
      });

      setLoadingAction(false);
      await handleOpenSweetAlert('Solicitud rechazada correctamente.', 'success');
      fetchData();
    } catch (error) {
      setLoadingAction(false);
      await handleOpenSweetAlert('Error al cancelar la solicitud.', 'error');
    } finally {
      setLoadingAction(false);
    }
  }, [modalStates, loggedInUsername, fetchData]);

  const handleUploadAdjuntos = useCallback(async () => {
    if (!selectedRow || adjuntos.length === 0) return;

    const formData = new FormData();
    formData.append('ID_SOLICITUD', selectedRow.ID);
    formData.append('FOLIO', selectedRow.FOLIO || selectedRow.FOLIO_OC);
    formData.append('NOMBRE_EMPRESA', nombreEmpresa);
    formData.append('USUARIO', loggedInUsername);
    formData.append('ES_REEMPLAZO', selectedRow.ESTATUS === 'Pagada' ? '1' : '0');

    adjuntos.forEach((file) => {
      formData.append('adjunto', file);
    });

    try {
      setLoadingAction(true);
      await axios.put(`${apiUrl}/tesoreroGuardarAdjuntos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setFileUploadModalOpen(false);
      setAdjuntos([]);
      setLoadingAction(false);

      await handleOpenSweetAlert(
        selectedRow.ESTATUS === 'Pagada'
          ? 'Comprobante de pago reemplazado correctamente.'
          : 'Comprobante de pago cargado correctamente.',
        'success'
      );
      fetchData();
    } catch (error) {
      setLoadingAction(false);
      console.error('Error al guardar comprobante:', error);
      await handleOpenSweetAlert('No se pudo guardar el comprobante.', 'error');
    } finally {
      setLoadingAction(false);
    }
  }, [adjuntos, selectedRow, nombreEmpresa, loggedInUsername, apiUrl, fetchData]);

  const buttons = useMemo(() => ([
    {
      id: 8,
      label: 'Cancelar Solicitud',
      onClick: (row) => handleCancelarAutorizacion(row),
      icon: <DoDisturbOffIcon />,
      buttonColor: '#d32f2f', // Rojo
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: (row) => ['Cancelada', 'Cancelada ERP', 'Pagada'].includes(row.ESTATUS),
    },
    {
      id: 7,
      label: (row) => row.ESTATUS === 'Pagada' ? 'Reemplazar comprobante' : 'Subir Comprobante',
      onClick: (row) => { setSelectedRow(row); setFileUploadModalOpen(true); },
      icon: <AttachFileIcon />,
      buttonColor: '#ff9800', // Naranja/Ambar
      buttonFontColor: '#ffffff',
      isVisible: true,
      isDisabled: (row) => !['Autorizada', 'Pagada'].includes(row.ESTATUS),
    },
    {
      id: 2,
      label: 'Visualizar Archivos',
      onClick: (row) => handleViewFile(row),
      icon: <FolderIcon />,
      buttonColor: '#607D8B',
      buttonFontColor: '#E3F2FD',
      isVisible: true,
      isDisabled: false,
    },
    {
      id: 6,
      label: 'Orden de Compra (OC)',
      onClick: (row) => handleViewSpecificFile(row, row.ID_OC),
      icon: <AssignmentTurnedInIcon />,
      buttonColor: '#ed6c02', // Naranja
      buttonFontColor: '#ffffff',
      isVisible: !esCompras,
      isDisabled: (row) => !row.HAS_OC,
    },
    {
      id: 5,
      label: 'Carátula Bancaria',
      onClick: (row) => handleViewSpecificFile(row, row.ID_CB),
      icon: <AccountBalanceIcon />,
      buttonColor: '#0288d1', // Azul
      buttonFontColor: '#ffffff',
      isVisible: !esCompras,
      isDisabled: (row) => !row.HAS_CB,
    },
    {
      id: 4,
      label: 'Factura',
      onClick: (row) => handleViewSpecificFile(row, row.ID_FACTURA),
      icon: <ReceiptIcon />,
      buttonColor: '#2e7d32', // Verde
      buttonFontColor: '#ffffff',
      isVisible: !esCompras,
      isDisabled: (row) => !row.HAS_FACTURA,
    },
    {
      id: 3,
      label: 'Comprobante de pago',
      onClick: (row) => handleViewPagoFile(row),
      icon: <ReceiptLongIcon />,
      buttonColor: '#4CAF50',
      buttonFontColor: '#ffffff',
      isVisible: !esCompras,
      isDisabled: (row) => row.ESTATUS !== 'Pagada',
    },
  ]), [handleViewFile, handleViewPagoFile, handleViewSpecificFile, handleCancelarAutorizacion, esCompras]);


  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        gap: 0.8,
        p: 'clamp(6px, 0.8vw, 12px)',
        bgcolor: '#f8fafc'
      }}
    >
      {error && <div style={{ color: 'red' }}>{error}</div>}



      {/* ── BARRA SUPERIOR DE ACCESO RÁPIDO & TOGGLE DE FILTROS ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.2, gap: 1, width: '100%', minWidth: 0, maxWidth: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, maxWidth: '100%' }}>

          {/* Vista Móvil Ergonomica (<md): Fila 1 (Buscador expandido + Jerárquico + Más Filtros + KPIs) / Fila 2 (Fechas + Estatus + Excel + Restablecer) */}
          <Box
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexDirection: 'column',
              gap: 0.8,
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
              boxSizing: 'border-box',
              bgcolor: '#ffffff',
              p: 0.8,
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 4px rgba(15, 23, 42, 0.03)',
            }}
          >
            {/* Fila 1 Móvil: Buscador expandido + Jerarquía + Filtros secundarios + KPIs */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, width: '100%', minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
              <TextField
                disabled={loadingData}
                size="small"
                placeholder="Buscar OC, folio, prov..."
                value={quickSearch}
                onChange={(e) => setQuickSearch(e.target.value)}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyFilters(tempFilterData, e.currentTarget.value);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#0655A5', fontSize: 16 }} />
                    </InputAdornment>
                  ),
                  endAdornment: quickSearch ? (
                    <InputAdornment position="end">
                      <IconButton
                        disabled={loadingData}
                        size="small"
                        onClick={() => {
                          setQuickSearch('');
                          applyFilters(tempFilterData, '');
                        }}
                        sx={{ p: 0.2 }}
                      >
                        <ClearIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '& .MuiOutlinedInput-root': {
                    height: 34,
                    borderRadius: '10px',
                    bgcolor: '#f8fafc',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    px: 0.8,
                    '& fieldset': { borderColor: '#cbd5e1' },
                    '&:hover fieldset': { borderColor: '#94a3b8' },
                    '&.Mui-focused fieldset': { borderColor: '#0655A5', bgcolor: '#ffffff' },
                  }
                }}
              />

              {/* Botón Jerarquía */}
              <Tooltip title={`Jerarquía: ${hierarchicalSummary}`}>
                <Button
                  size="small"
                  variant={hasHierarchicalFilter ? 'contained' : 'outlined'}
                  onClick={(e) => setHierarchicalAnchorEl(e.currentTarget)}
                  sx={{
                    minWidth: 34,
                    width: 34,
                    height: 34,
                    p: 0,
                    flexShrink: 0,
                    borderRadius: '10px',
                    bgcolor: hasHierarchicalFilter ? '#eff6ff' : '#ffffff',
                    borderColor: hasHierarchicalFilter ? '#93c5fd' : '#cbd5e1',
                    color: hasHierarchicalFilter ? '#1e40af' : '#475569',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: hasHierarchicalFilter ? '#dbeafe' : '#f8fafc' },
                  }}
                >
                  <AccountTreeIcon sx={{ fontSize: 17 }} />
                </Button>
              </Tooltip>

              {/* Botón Filtros Complementarios con Badge */}
              <Tooltip title="Otros filtros (Depto, Prov, Divisa)">
                <Badge
                  badgeContent={extraFiltersCount}
                  color="primary"
                  sx={{
                    flexShrink: 0,
                    '& .MuiBadge-badge': {
                      fontSize: '0.62rem',
                      height: 16,
                      minWidth: 16,
                    }
                  }}
                >
                  <Button
                    size="small"
                    variant={showFiltersPanel || extraFiltersCount > 0 ? 'contained' : 'outlined'}
                    onClick={(e) => {
                      setFilterAnchorEl(e.currentTarget);
                      setShowFiltersPanel(prev => !prev);
                    }}
                    sx={{
                      minWidth: 34,
                      width: 34,
                      height: 34,
                      p: 0,
                      flexShrink: 0,
                      borderRadius: '10px',
                      bgcolor: showFiltersPanel ? '#0f172a' : extraFiltersCount > 0 ? '#0655A5' : '#ffffff',
                      borderColor: extraFiltersCount > 0 ? '#0655A5' : '#cbd5e1',
                      color: showFiltersPanel || extraFiltersCount > 0 ? '#ffffff' : '#475569',
                      boxShadow: 'none',
                      '&:hover': { bgcolor: showFiltersPanel ? '#1e293b' : '#054785' },
                    }}
                  >
                    <FilterListIcon sx={{ fontSize: 17 }} />
                  </Button>
                </Badge>
              </Tooltip>

              {/* Botón KPIs Toggle */}
              <Tooltip title={showAnalyticsCards ? 'Ocultar Analíticas' : 'Ver Analíticas'}>
                <Button
                  size="small"
                  variant={showAnalyticsCards ? 'contained' : 'outlined'}
                  onClick={() => setShowAnalyticsCards(prev => !prev)}
                  sx={{
                    minWidth: 34,
                    width: 34,
                    height: 34,
                    p: 0,
                    flexShrink: 0,
                    borderRadius: '10px',
                    bgcolor: showAnalyticsCards ? '#0284c7' : '#ffffff',
                    borderColor: showAnalyticsCards ? '#0284c7' : '#cbd5e1',
                    color: showAnalyticsCards ? '#ffffff' : '#0284c7',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: showAnalyticsCards ? '#0369a1' : '#f0f9ff' },
                  }}
                >
                  <AnalyticsIcon sx={{ fontSize: 17 }} />
                </Button>
              </Tooltip>
            </Box>

            {/* Fila 2 Móvil: Rango de fechas horizontal + Menú Estatus + Excel + Restablecer */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflowX: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                WebkitOverflowScrolling: 'touch',
                pb: 0.2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.4,
                  bgcolor: '#f8fafc',
                  px: 0.8,
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  height: 28,
                  flexShrink: 0,
                  '&:hover': { borderColor: '#94a3b8' },
                }}
              >
                <CalendarTodayIcon sx={{ fontSize: 13, color: '#0655A5', mr: 0.2 }} />
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b' }}>Del</Typography>
                <TextField
                  disabled={loadingData}
                  size="small"
                  type="date"
                  value={tempFilterData.FECHA_INICIO}
                  onChange={(e) => updateVisibleFilter('FECHA_INICIO', e.target.value, { apply: true })}
                  onClick={(e) => {
                    try {
                      if (e.target?.showPicker) e.target.showPicker();
                    } catch {}
                  }}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    width: 78,
                    '& .MuiInputBase-input': {
                      fontSize: '0.70rem',
                      fontWeight: 600,
                      color: '#0f172a',
                      py: 0,
                      cursor: 'pointer',
                      '&::-webkit-calendar-picker-indicator': { display: 'none' },
                      '&::-webkit-inner-spin-button': { display: 'none' },
                    },
                  }}
                />
                <Typography sx={{ fontSize: '0.66rem', fontWeight: 700, color: '#64748b' }}>al</Typography>
                <TextField
                  disabled={loadingData}
                  size="small"
                  type="date"
                  value={tempFilterData.FECHA_FIN}
                  onChange={(e) => updateVisibleFilter('FECHA_FIN', e.target.value, { apply: true })}
                  onClick={(e) => {
                    try {
                      if (e.target?.showPicker) e.target.showPicker();
                    } catch {}
                  }}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{
                    width: 78,
                    '& .MuiInputBase-input': {
                      fontSize: '0.70rem',
                      fontWeight: 600,
                      color: '#0f172a',
                      py: 0,
                      cursor: 'pointer',
                      '&::-webkit-calendar-picker-indicator': { display: 'none' },
                      '&::-webkit-inner-spin-button': { display: 'none' },
                    },
                  }}
                />
              </Box>

              <TableFilterChips
                items={statusChipsMemo}
                activeFilter={tempFilterData.ESTATUS || ''}
                onFilterClick={handleStatusFilterClick}
                disabled={loadingData}
                isCompact={true}
                compactLabel="Estatus"
              />

              <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                <ExportButton
                  data={dataForExport}
                  onFetchAll={handleExportFetchAll}
                  columnLabels={exportLabelsExtended}
                  hiddenFields={hiddenFieldsMemo}
                  fileName="Solicitudes_Programadas.xlsx"
                  hidePendientes={true}
                  tableLoading={loadingData}
                  btnSx={{
                    width: 28,
                    height: 28,
                    borderRadius: '8px',
                    border: '1px solid #86efac',
                    bgcolor: '#f0fdf4',
                    color: '#16a34a',
                    boxShadow: 'none',
                    p: 0,
                  }}
                />
              </Box>

              {hasAnyActiveFilter && (
                <Button
                  size="small"
                  variant="text"
                  onClick={resetFilters}
                  startIcon={<RestartAltIcon sx={{ fontSize: 13 }} />}
                  sx={{
                    height: 28,
                    px: 0.8,
                    fontSize: '0.66rem',
                    fontWeight: 700,
                    color: '#dc2626',
                    bgcolor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    textTransform: 'none',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': { bgcolor: '#fee2e2' },
                  }}
                >
                  Restablecer
                </Button>
              )}
            </Box>
          </Box>

          {/* Vista Laptop (md a xl): Fechas -> Estatus -> Filtros Jerárquicos -> Más Filtros -> Buscador */}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex', xl: 'none' },
              alignItems: 'center',
              gap: 0.6,
              flex: 1,
              bgcolor: '#ffffff',
              p: 0.4,
              px: 0.8,
              borderRadius: '14px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 4px rgba(15, 23, 42, 0.03)',
              overflowX: 'auto',
            }}
          >
            {/* 1. Rango de Fechas Integrado Estilo Cápsula */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.4,
                bgcolor: '#ffffff',
                px: 0.7,
                borderRadius: '10px',
                border: '1px solid #cbd5e1',
                height: 26,
                flexShrink: 0,
                '&:hover': { borderColor: '#94a3b8' },
              }}
            >
              <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b' }}>Del</Typography>
              <TextField
                disabled={loadingData}
                size="small"
                type="date"
                value={tempFilterData.FECHA_INICIO}
                onChange={(e) => updateVisibleFilter('FECHA_INICIO', e.target.value, { apply: true })}
                onClick={(e) => {
                  try {
                    if (e.target?.showPicker) e.target.showPicker();
                  } catch {}
                }}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  width: 78,
                  '& .MuiInputBase-input': {
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: '#0f172a',
                    py: 0,
                    cursor: 'pointer',
                    '&::-webkit-calendar-picker-indicator': {
                      display: 'none',
                    },
                    '&::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                  },
                }}
              />
              <Typography sx={{ fontSize: '0.64rem', fontWeight: 700, color: '#64748b' }}>al</Typography>
              <TextField
                disabled={loadingData}
                size="small"
                type="date"
                value={tempFilterData.FECHA_FIN}
                onChange={(e) => updateVisibleFilter('FECHA_FIN', e.target.value, { apply: true })}
                onClick={(e) => {
                  try {
                    if (e.target?.showPicker) e.target.showPicker();
                  } catch {}
                }}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  width: 78,
                  '& .MuiInputBase-input': {
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    color: '#0f172a',
                    py: 0,
                    cursor: 'pointer',
                    '&::-webkit-calendar-picker-indicator': {
                      display: 'none',
                    },
                    '&::-webkit-inner-spin-button': {
                      display: 'none',
                    },
                  },
                }}
              />
            </Box>

            {/* 2. Menú Desplegable de Estatus */}
            <TableFilterChips
              items={statusChipsMemo}
              activeFilter={tempFilterData.ESTATUS || ''}
              onFilterClick={handleStatusFilterClick}
              disabled={loadingData}
              isCompact={true}
            />

            {/* 3. Selector Jerárquico Estilo Breadcrumbs: Empresa / Proyecto / Grupo / CC */}
            <Button
              size="small"
              variant="outlined"
              onClick={(e) => {
                setHierarchicalAnchorEl(e.currentTarget);
              }}
              startIcon={<AccountTreeIcon sx={{ fontSize: '13px !important', color: hasHierarchicalFilter ? '#0655A5' : '#64748b' }} />}
              endIcon={hierarchicalAnchorEl ? <ExpandLessIcon sx={{ fontSize: 13 }} /> : <ExpandMoreIcon sx={{ fontSize: 13 }} />}
              sx={{
                height: 26,
                px: 1,
                maxWidth: 240,
                fontSize: '0.68rem',
                fontWeight: hasHierarchicalFilter ? 700 : 500,
                borderRadius: '10px',
                bgcolor: hasHierarchicalFilter ? '#eff6ff' : '#ffffff',
                borderColor: hasHierarchicalFilter ? '#93c5fd' : '#cbd5e1',
                color: hasHierarchicalFilter ? '#1e40af' : '#334155',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                '&:hover': { bgcolor: hasHierarchicalFilter ? '#dbeafe' : '#f8fafc', borderColor: '#60a5fa' },
              }}
            >
              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {hierarchicalSummary}
              </Box>
            </Button>

            {/* 4. Botón Más Filtros (Slim) */}
            <Button
              size="small"
              variant={showFiltersPanel ? 'contained' : 'outlined'}
              onClick={(e) => {
                setFilterAnchorEl(e.currentTarget);
                setShowFiltersPanel(prev => !prev);
              }}
              startIcon={<FilterListIcon sx={{ fontSize: '13px !important' }} />}
              endIcon={showFiltersPanel ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
              sx={{
                height: 26,
                px: 0.8,
                fontSize: '0.68rem',
                fontWeight: 600,
                borderRadius: '10px',
                bgcolor: showFiltersPanel ? '#0f172a' : '#ffffff',
                borderColor: '#cbd5e1',
                color: showFiltersPanel ? '#ffffff' : '#334155',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                '&:hover': { bgcolor: showFiltersPanel ? '#1e293b' : '#f8fafc', borderColor: '#94a3b8' },
              }}
            >
              Más {((tempFilterData.DEPARTAMENTOS || []).length || (tempFilterData.PROVEEDORES || []).length || (tempFilterData.DIVISAS || []).length) ? '•' : ''}
            </Button>

            {/* 5. Buscador */}
            <TextField
              disabled={loadingData}
              size="small"
              placeholder="Buscar..."
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyFilters(tempFilterData, e.currentTarget.value);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#0655A5', fontSize: 13 }} />
                  </InputAdornment>
                ),
                endAdornment: quickSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={loadingData}
                      size="small"
                      onClick={() => {
                        setQuickSearch('');
                        applyFilters(tempFilterData, '');
                      }}
                      sx={{ p: 0.1 }}
                    >
                      <ClearIcon sx={{ fontSize: 11 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                width: { xs: 110, sm: 130, md: 145 },
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  height: 26,
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  px: 0.8,
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#0655A5' },
                }
              }}
            />

          </Box>

          {/* Vista Escritorio (xl en adelante): FILTROS A LA IZQUIERDA -> DIVISOR -> BUSCADOR GENERAL A LA DERECHA */}
          <Box
            sx={{
              display: { xs: 'none', xl: 'flex' },
              alignItems: 'center',
              gap: 'clamp(4px, 0.5vw, 7px)',
              bgcolor: '#ffffff',
              p: 'clamp(3px, 0.4vw, 5px)',
              px: 'clamp(6px, 0.8vw, 12px)',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 2px 6px rgba(15, 23, 42, 0.03)',
              flex: 1,
              maxWidth: '100%',
              overflow: 'hidden'
            }}
          >
            {/* 1. Rango de Fechas Integrado Estilo Cápsula */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, bgcolor: '#ffffff', px: 0.8, py: 0.1, borderRadius: '10px', border: '1px solid #cbd5e1', height: 'clamp(26px, 2vw, 28px)', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 700, color: '#64748b' }}>Del</Typography>
              <TextField
                disabled={loadingData}
                size="small"
                type="date"
                value={tempFilterData.FECHA_INICIO}
                onChange={(e) => updateVisibleFilter('FECHA_INICIO', e.target.value, { apply: false })}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{ width: 96, '& .MuiInputBase-input': { fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 600, color: '#0f172a', py: 0 } }}
              />
              <Typography sx={{ fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 700, color: '#64748b' }}>al</Typography>
              <TextField
                disabled={loadingData}
                size="small"
                type="date"
                value={tempFilterData.FECHA_FIN}
                onChange={(e) => updateVisibleFilter('FECHA_FIN', e.target.value, { apply: false })}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{ width: 96, '& .MuiInputBase-input': { fontSize: 'clamp(0.62rem, 0.58rem + 0.06vw, 0.68rem)', fontWeight: 600, color: '#0f172a', py: 0 } }}
              />
            </Box>

            {/* 2. Select Empresa */}
            <Autocomplete
              disabled={loadingData}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={empresaOptions}
              value={empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id))}
              isOptionEqualToValue={(option, val) => option?.id === val?.id}
              onChange={(_, value) => {
                const updated = {
                  ...tempFilterData,
                  EMPRESA_IDS: (value || []).map(empresa => empresa.id),
                  EMPRESA: (value || []).map(empresa => empresa.nombre).join(','),
                  PROYECTOS: [],
                  PROYECTO: '',
                  DIVISIONES: [],
                  DIVISION: '',
                  CENTRO_COSTOS: [],
                  CENTRO_COSTO: '',
                  DEPARTAMENTOS: [],
                  PROVEEDORES: [],
                  DIVISAS: [],
                };
                setTempFilterData(updated);
                setFilterData(updated);
                setTableParams(prev => ({ ...prev, page: 1 }));
              }}
              limitTags={1}
              renderTags={() => null}
              getOptionLabel={(option) => option?.nombre || ''}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(
                  params,
                  'Empresa',
                  empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id)).map(e => e.nombre)
                )
              )}
              sx={{ width: 'clamp(100px, 8.5vw, 125px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 3. Select Proyecto (Nivel 2) */}
            <Autocomplete
              disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={proyectoOptions}
              value={proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id))}
              isOptionEqualToValue={(option, val) => option?.id === val?.id}
              onChange={(_, value) => {
                const updated = {
                  ...tempFilterData,
                  PROYECTOS: (value || []).map(p => p.id),
                  PROYECTO: (value || []).map(p => p.nombre).join(','),
                  DIVISIONES: [],
                  DIVISION: '',
                  CENTRO_COSTOS: [],
                  CENTRO_COSTO: ''
                };
                setTempFilterData(updated);
              }}
              limitTags={1}
              renderTags={() => null}
              getOptionLabel={(option) => option?.nombre || ''}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(
                  params,
                  'Proyecto',
                  proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id)).map(p => p.nombre)
                )
              )}
              sx={{ width: 'clamp(95px, 8vw, 118px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 4. Select Grupo / División (Nivel 3) */}
            <Autocomplete
              disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
              multiple
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={divisionOptions}
              value={divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id))}
              isOptionEqualToValue={(option, val) => option?.id === val?.id}
              onChange={(_, value) => {
                const updated = {
                  ...tempFilterData,
                  DIVISIONES: (value || []).map(d => d.id),
                  DIVISION: (value || []).map(d => d.nombre).join(','),
                  CENTRO_COSTOS: [],
                  CENTRO_COSTO: ''
                };
                setTempFilterData(updated);
              }}
              limitTags={1}
              renderTags={() => null}
              getOptionLabel={(option) => option?.nombre || ''}
              renderOption={renderCheckboxOption}
              renderInput={(params) => (
                renderSummaryInput(
                  params,
                  'Grupo',
                  divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id)).map(d => d.nombre)
                )
              )}
              sx={{ width: 'clamp(95px, 8vw, 115px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 5. Select Centro de Costo (Nivel 4) */}
            <Autocomplete
              disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
              multiple
              freeSolo
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={centroCostoOptions}
              value={tempFilterData.CENTRO_COSTOS || []}
              onChange={(_, value) => updateVisibleFilter('CENTRO_COSTOS', value || [], { apply: false })}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(params, 'Centro costo', tempFilterData.CENTRO_COSTOS || [])}
              sx={{ width: 'clamp(100px, 9vw, 125px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 6. Select Departamento */}
            <Autocomplete
              disabled={loadingData}
              multiple
              freeSolo
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={departamentoOptions}
              value={tempFilterData.DEPARTAMENTOS || []}
              onChange={(_, value) => updateVisibleFilter('DEPARTAMENTOS', value || [], { apply: false })}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(params, 'Departamento', tempFilterData.DEPARTAMENTOS || [])}
              sx={{ width: 'clamp(100px, 9vw, 125px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 7. Select Proveedor */}
            <Autocomplete
              disabled={loadingData}
              multiple
              freeSolo
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={proveedorOptions}
              value={tempFilterData.PROVEEDORES || []}
              onChange={(_, value) => updateVisibleFilter('PROVEEDORES', value || [], { apply: false })}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(params, 'Proveedor', tempFilterData.PROVEEDORES || [])}
              sx={{ width: 'clamp(105px, 9.5vw, 130px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* 8. Select Divisa */}
            <Autocomplete
              disabled={loadingData}
              multiple
              freeSolo
              disableCloseOnSelect
              forcePopupIcon
              size="small"
              options={divisaOptions}
              value={tempFilterData.DIVISAS || []}
              onChange={(_, value) => updateVisibleFilter('DIVISAS', value || [], { apply: false })}
              limitTags={1}
              renderTags={() => null}
              renderOption={renderCheckboxOption}
              renderInput={(params) => renderSummaryInput(params, 'Divisa', tempFilterData.DIVISAS || [])}
              sx={{ width: 'clamp(75px, 6vw, 90px)', flexShrink: 0, ...compactAutocompleteSx }}
            />

            {/* Separador vertical */}
            <Box sx={{ borderLeft: '1px solid #cbd5e1', height: 20, mx: 0.3, flexShrink: 0 }} />

            {/* 9. Buscador General al final */}
            <TextField
              disabled={loadingData}
              size="small"
              placeholder="Buscar..."
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyFilters(tempFilterData, e.currentTarget.value);
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#0655A5', fontSize: 'clamp(12px, 0.9vw, 14px)' }} />
                  </InputAdornment>
                ),
                endAdornment: quickSearch ? (
                  <InputAdornment position="end">
                    <IconButton
                      disabled={loadingData}
                      size="small"
                      onClick={() => {
                        setQuickSearch('');
                        applyFilters(tempFilterData, '');
                      }}
                      sx={{ p: 0.1 }}
                    >
                      <ClearIcon sx={{ fontSize: 'clamp(10px, 0.8vw, 12px)' }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                width: 'clamp(110px, 9vw, 135px)',
                flexShrink: 0,
                '& .MuiOutlinedInput-root': {
                  height: 'clamp(26px, 2vw, 28px)',
                  borderRadius: '10px',
                  bgcolor: '#ffffff',
                  fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.70rem)',
                  fontWeight: 500,
                  px: 0.8,
                  '& fieldset': { borderColor: '#cbd5e1' },
                  '&:hover fieldset': { borderColor: '#94a3b8' },
                  '&.Mui-focused fieldset': { borderColor: '#0655A5', borderWidth: '1.5px' },
                }
              }}
            />

            {/* 10. Botón Buscar */}
            <Button
              size="small"
              variant="contained"
              disabled={loadingData}
              onClick={() => applyFilters(tempFilterData, quickSearch)}
              startIcon={<SearchIcon sx={{ fontSize: 'clamp(11px, 0.8vw, 13px) !important' }} />}
              sx={{
                height: 'clamp(26px, 2vw, 28px)',
                px: 'clamp(8px, 0.8vw, 12px)',
                fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.70rem)',
                fontWeight: 700,
                borderRadius: '10px',
                bgcolor: '#0655A5',
                color: '#ffffff',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                '&:hover': { bgcolor: '#054785' },
              }}
            >
              Buscar
            </Button>

            {/* 11. Botón Limpiar */}
            <Button
              size="small"
              variant="outlined"
              disabled={loadingData}
              onClick={() => {
                setTempFilterData(baseFilters);
                setFilterData(baseFilters);
                setQuickSearch('');
                setTableParams(prev => ({ ...prev, globalQuery: '', page: 1 }));
              }}
              startIcon={<ClearIcon sx={{ fontSize: 'clamp(11px, 0.8vw, 13px) !important' }} />}
              sx={{
                height: 'clamp(26px, 2vw, 28px)',
                px: 'clamp(6px, 0.6vw, 10px)',
                fontSize: 'clamp(0.64rem, 0.60rem + 0.08vw, 0.70rem)',
                fontWeight: 600,
                borderRadius: '10px',
                borderColor: '#cbd5e1',
                color: '#64748b',
                textTransform: 'none',
                boxShadow: 'none',
                flexShrink: 0,
                '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' },
              }}
            >
              Limpiar
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.8 }}>

          <Tooltip title={showAnalyticsCards ? 'Ocultar Tarjetas Analíticas' : 'Ver Tarjetas Analíticas (Dona / Porcentajes)'}>
            <Button
              size="small"
              variant={showAnalyticsCards ? 'contained' : 'outlined'}
              onClick={() => setShowAnalyticsCards(prev => !prev)}
              startIcon={<AnalyticsIcon sx={{ fontSize: '13px !important' }} />}
              endIcon={showAnalyticsCards ? <ExpandLessIcon sx={{ fontSize: 15 }} /> : <ExpandMoreIcon sx={{ fontSize: 15 }} />}
              sx={{
                height: { xs: 24, sm: 26 },
                px: 1,
                fontSize: '0.72rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '14px',
                bgcolor: showAnalyticsCards ? '#0284c7' : '#ffffff',
                borderColor: showAnalyticsCards ? '#0284c7' : '#cbd5e1',
                color: showAnalyticsCards ? '#fff' : '#0284c7',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: showAnalyticsCards ? '#0369a1' : '#f0f9ff',
                  borderColor: '#0284c7',
                }
              }}
            >
              KPIs
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Popover 1: Selector Jerárquico Empresa > Proyecto > Grupo > CC */}
      <Popover
        open={Boolean(hierarchicalAnchorEl)}
        anchorEl={hierarchicalAnchorEl}
        onClose={() => setHierarchicalAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 10,
          sx: {
            mt: 0.8,
            p: 2,
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            width: 360,
            maxWidth: '90vw',
            boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountTreeIcon sx={{ color: '#0655A5', fontSize: 18 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
              Filtro por Jerarquía
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setHierarchicalAnchorEl(null)}>
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* 1. Empresa */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            1. EMPRESA
          </Typography>
          <Autocomplete
            disabled={loadingData}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={empresaOptions}
            value={empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id))}
            onChange={(_, value) => {
              const updated = {
                ...tempFilterData,
                EMPRESA_IDS: (value || []).map(empresa => empresa.id),
                EMPRESA: (value || []).map(empresa => empresa.nombre).join(','),
                PROYECTOS: [],
                PROYECTO: '',
                DIVISIONES: [],
                DIVISION: '',
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setTempFilterData(updated);
              setFilterData(updated);
              setTableParams(prev => ({ ...prev, page: 1 }));
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Empresa', empresaOptions.filter(empresa => (tempFilterData.EMPRESA_IDS || []).includes(empresa.id)).map(e => e.nombre))}
            fullWidth
          />
        </Box>

        {/* 2. Proyecto */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            2. PROYECTO
          </Typography>
          <Autocomplete
            disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={proyectoOptions}
            value={proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id))}
            onChange={(_, value) => {
              const updated = {
                ...tempFilterData,
                PROYECTOS: (value || []).map(p => p.id),
                PROYECTO: (value || []).map(p => p.nombre).join(','),
                DIVISIONES: [],
                DIVISION: '',
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setTempFilterData(updated);
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Proyecto', proyectoOptions.filter(p => (tempFilterData.PROYECTOS || []).includes(p.id)).map(p => p.nombre))}
            fullWidth
          />
        </Box>

        {/* 3. Grupo / División */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            3. GRUPO / DIVISIÓN
          </Typography>
          <Autocomplete
            disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={divisionOptions}
            value={divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id))}
            onChange={(_, value) => {
              const updated = {
                ...tempFilterData,
                DIVISIONES: (value || []).map(d => d.id),
                DIVISION: (value || []).map(d => d.nombre).join(','),
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setTempFilterData(updated);
            }}
            getOptionLabel={(option) => option?.nombre || ''}
            isOptionEqualToValue={(option, val) => option?.id === val?.id}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Grupo / División', divisionOptions.filter(d => (tempFilterData.DIVISIONES || []).includes(d.id)).map(d => d.nombre))}
            fullWidth
          />
        </Box>

        {/* 4. Centro de Costo */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            4. CENTRO DE COSTO
          </Typography>
          <Autocomplete
            disabled={loadingData || !(tempFilterData.EMPRESA_IDS || []).length}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={centroCostoOptions}
            value={tempFilterData.CENTRO_COSTOS || []}
            onChange={(_, value) => updateVisibleFilter('CENTRO_COSTOS', value || [], { apply: false })}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Centro costo', tempFilterData.CENTRO_COSTOS || [])}
            fullWidth
          />
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.5, pt: 1, borderTop: '1px solid #f1f5f9' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const cleared = {
                ...tempFilterData,
                EMPRESA_IDS: [],
                EMPRESA: '',
                PROYECTOS: [],
                PROYECTO: '',
                DIVISIONES: [],
                DIVISION: '',
                CENTRO_COSTOS: [],
                CENTRO_COSTO: '',
              };
              setTempFilterData(cleared);
              setFilterData(cleared);
              setHierarchicalAnchorEl(null);
              applyFilters(cleared, quickSearch);
            }}
            sx={{ fontSize: 11, textTransform: 'none', borderRadius: '8px' }}
          >
            Limpiar Jerarquía
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              applyFilters(tempFilterData, quickSearch);
              setHierarchicalAnchorEl(null);
            }}
            sx={{ fontSize: 11, textTransform: 'none', bgcolor: '#0655A5', borderRadius: '8px', '&:hover': { bgcolor: '#044384' } }}
          >
            Aplicar
          </Button>
        </Stack>
      </Popover>

      {/* Popover 2: Filtros Complementarios / Más Filtros */}
      <Popover
        open={showFiltersPanel}
        anchorEl={filterAnchorEl}
        onClose={() => setShowFiltersPanel(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 10,
          sx: {
            mt: 0.8,
            p: 2,
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            width: 320,
            maxWidth: '90vw',
            boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterListIcon sx={{ color: '#0655A5', fontSize: 18 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
              Más Filtros
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setShowFiltersPanel(false)}>
            <ClearIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>

        {/* 1. Departamento */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            DEPARTAMENTO
          </Typography>
          <Autocomplete
            disabled={loadingData}
            multiple
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={departamentoOptions}
            value={tempFilterData.DEPARTAMENTOS || []}
            onChange={(_, value) => updateVisibleFilter('DEPARTAMENTOS', value || [], { apply: false })}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Departamento', tempFilterData.DEPARTAMENTOS || [])}
            fullWidth
          />
        </Box>

        {/* 2. Proveedor */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            PROVEEDOR
          </Typography>
          <Autocomplete
            disabled={loadingData}
            multiple
            freeSolo
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={proveedorOptions}
            value={tempFilterData.PROVEEDORES || []}
            onChange={(_, value) => updateVisibleFilter('PROVEEDORES', value || [], { apply: false })}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Proveedor', tempFilterData.PROVEEDORES || [])}
            fullWidth
          />
        </Box>

        {/* 3. Divisa */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mb: 0.5 }}>
            DIVISA
          </Typography>
          <Autocomplete
            disabled={loadingData}
            multiple
            freeSolo
            disableCloseOnSelect
            forcePopupIcon
            size="small"
            options={divisaOptions}
            value={tempFilterData.DIVISAS || []}
            onChange={(_, value) => updateVisibleFilter('DIVISAS', value || [], { apply: false })}
            renderOption={renderCheckboxOption}
            renderInput={(params) => renderSummaryInput(params, 'Divisa', tempFilterData.DIVISAS || [])}
            fullWidth
          />
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 0.5, pt: 1, borderTop: '1px solid #f1f5f9' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              const cleared = {
                ...tempFilterData,
                DEPARTAMENTOS: [],
                PROVEEDORES: [],
                DIVISAS: [],
              };
              setTempFilterData(cleared);
              setFilterData(cleared);
              setShowFiltersPanel(false);
              applyFilters(cleared, quickSearch);
            }}
            sx={{ fontSize: 11, textTransform: 'none', borderRadius: '8px' }}
          >
            Limpiar
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              applyFilters(tempFilterData, quickSearch);
              setShowFiltersPanel(false);
            }}
            sx={{ fontSize: 11, textTransform: 'none', bgcolor: '#0655A5', borderRadius: '8px', '&:hover': { bgcolor: '#044384' } }}
          >
            Aplicar
          </Button>
        </Stack>
      </Popover>

      {/* ── PANEL DE TARJETAS BENTO ANALYTICS (ULTRA-COMPACT EXECUTIVE RIBBON) ── */}
      <Collapse in={showAnalyticsCards} unmountOnExit>
        <Box sx={{ mb: 1, px: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {data?.totalesPorDivisa && Object.keys(data.totalesPorDivisa).length > 0 ? (
              Object.entries(data.totalesPorDivisa).map(([divisa, vals]) => {
                const total = vals.total || 0;
                const solicitado = vals.solicitado || 0;
                const restante = total > solicitado ? total - solicitado : (vals.restante || 0);
                const pctSolicitado = total > 0 ? Math.min(100, Math.round((solicitado / total) * 100)) : 0;

                const colorMain = divisa === 'USD' ? '#d97706' : divisa === 'EUR' ? '#4f46e5' : '#0284c7';
                const bgHeader = divisa === 'USD' ? '#fffbeb' : divisa === 'EUR' ? '#eef2ff' : '#f0f9ff';
                const borderCard = divisa === 'USD' ? '#fde68a' : divisa === 'EUR' ? '#c7d2fe' : '#bae6fd';

                return (
                  <Paper
                    key={divisa}
                    elevation={0}
                    sx={{
                      px: 1.2,
                      py: 0.4,
                      borderRadius: '8px',
                      border: `1px solid ${borderCard}`,
                      bgcolor: '#ffffff',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      flex: '1 1 240px',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={divisa}
                        size="small"
                        sx={{
                          fontWeight: 900,
                          fontSize: 11,
                          bgcolor: bgHeader,
                          color: colorMain,
                          border: `1px solid ${borderCard}`
                        }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={{ fontSize: 9, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', lineHeight: 1 }}>
                          Total OC ({vals.count || 0})
                        </Typography>
                        <Typography sx={{ fontSize: 15, fontWeight: 900, color: '#0f172a', mt: 0.2 }}>
                          ${total.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography sx={{ fontSize: 10, color: '#16a34a', fontWeight: 700 }}>
                          Solicitado: <strong>${solicitado.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        </Typography>
                        <Typography sx={{ fontSize: 10, color: '#ea580c', fontWeight: 700 }}>
                          Restante: <strong>${restante.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                        </Typography>
                      </Box>

                      {/* Mini Dona Radial */}
                      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress
                          variant="determinate"
                          value={100}
                          size={32}
                          thickness={5}
                          sx={{ color: '#e2e8f0', position: 'absolute' }}
                        />
                        <CircularProgress
                          variant="determinate"
                          value={pctSolicitado}
                          size={32}
                          thickness={5}
                          sx={{ color: colorMain, strokeLinecap: 'round' }}
                        />
                        <Box
                          sx={{
                            top: 0, left: 0, bottom: 0, right: 0,
                            position: 'absolute', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          <Typography variant="caption" component="div" sx={{ fontWeight: 900, fontSize: 8.5, color: '#0f172a' }}>
                            {`${pctSolicitado}%`}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                );
              })
            ) : null}
          </Box>
        </Box>
      </Collapse>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Tabla ocupa todo el espacio disponible */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <CollapsibleTable
            dataTable={dataForTable}

          leftButtons={
            /* Solo en pantalla grande (xl+): chips extendidos de colores en la franja de la tabla */
            <Box sx={{ display: { xs: 'none', xl: 'flex' }, alignItems: 'center' }}>
              <TableFilterChips
                items={statusChipsMemo}
                activeFilter={tempFilterData.ESTATUS}
                onFilterClick={handleStatusFilterClick}
                disabled={loadingData}
                isCompact={false}
              />
            </Box>
          }
          buttons={buttons}
          hiddenFields={tableHiddenFieldsMemo}
          columnLabels={tableColumnLabels}
          initialColumnOrder={compactColumnOrder}
          maxInlineActions={3}
          compactActions={true}
          density="small"
          loading={loadingData}
          showExportButton={true}
          enableSorting={true}
          enableFiltering={false}
          dataForExport={dataForExport}
          handleExportFetchAll={handleExportFetchAll}
          exportLabelsExtended={exportLabelsExtended}
          hiddenFieldsMemo={hiddenFieldsMemo}
          loadingData={loadingData}
          ExportButton={ExportButton}
          persistEnabled={true}
          persistKey="SolicitudesProgramadas"
          persistVista="layoutCompactV4"
          usuarioId={usuarioId}
          empresaId={empresaIdPersist}
          totalServerRows={data?.total || 0}
          serverPagination={true}
          onServerPaginationChange={({ page, pageSize }) => {
            setTableParams(prev => ({ ...prev, page, pageSize }));
          }}
          emptyStateData={emptyStateData}
          colorMode={colorMode}
          enableGlobalSearch={false}
          onStateChange={handleTableStateChange}
          onRowClick={handleOpenAuthModal}

          mobileCard={{ statusMode: 'none' }}
          renderMobileFooter={renderMobileFooterMemo}
          renderSummary={() => null}
          renderFooterRow={renderFooterRowMemo}
        />
        </Box>


      </Box>


      <SolicitudProgreso
        open={openAuthModal}
        onClose={() => setOpenAuthModal(false)}
        cadenaAut={selectedRow?.cadenaAut || []}
        estadoOrden={selectedRow?.ESTATUS}
        rowRaw={selectedRow}
        detalle={{
          folio: selectedRow?.FOLIO,
          solicitante: selectedRow?.SOLICITANTE || selectedRow?.REQUISITOR,
          centroCosto: selectedRow?.CENTROCOSTO,
          depart: selectedRow?.DEPARTAMENTO,
          pagoMet: selectedRow?.METODOPAGO,
          monto: selectedRow?.MONTOSOLICITADO,
          estatusOrd: selectedRow?.ESTATUS,
          fechaSoli: selectedRow?.FECHASOLICITUD,
          fechaPago: selectedRow?.FECHA_PAGO || selectedRow?.FECHAPAGO,
          vencimiento: selectedRow?.FECHA_VENCIMIENTO_REAL ?? selectedRow?.VENCIMIENTO,
          diasVencidos: selectedRow?.DIASRESTANTES ?? selectedRow?.DIAS_VENCIDOS ?? selectedRow?.DIAS_VENCIMIENTO,
        }}
      />

      <CancelacionModal
        open={modalStates.openCancelacionModal}
        onClose={() => setModalStates((prev) => ({ ...prev, openCancelacionModal: false }))}
        onConfirm={cancelarSolicitud}
        solicitudData={modalStates}
      />

      <FileUploadModal
        open={fileUploadModalOpen}
        onClose={() => { setFileUploadModalOpen(false); setAdjuntos([]); }}
        onFilesSelected={setAdjuntos}
        onSave={handleUploadAdjuntos}
      />

      <FileActionModal
        open={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        files={fileList}
        initialFileId={initialFileId}
      />

      <SimpleSuccessSpinner
        open={loadingAction}
        message="Procesando..."
      />

      {/* ── POPOVER AISLADO: su estado interno NO re-renderiza la tabla completa ── */}
      <TotalesPopover
        ref={totalesPopoverRef}
        totalesRef={dataTotalesRef}
      />
    </Box>
  );
}
